import { useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams, Link } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import { useDateLocale } from '@/hooks/useDateLocale';
import { Loader2, Calendar, FileSpreadsheet, Download, Printer, ChevronsUpDown, Check, MapPin } from 'lucide-react';
import { format, startOfDay, endOfDay, subDays, formatDuration, eachDayOfInterval } from 'date-fns';
import {
  ResponsiveContainer, ComposedChart, BarChart, Bar, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  Cell, ReferenceArea,
} from 'recharts';
import { DateRange } from 'react-day-picker';
import * as XLSX from 'xlsx';
import { DashboardLayout } from '@/components/dashboard/DashboardLayout';
import { useTraccarSession } from '@/hooks/useTraccarSession';
import { useBranding } from '@/hooks/useBranding';
import { useIsFlutterWebView } from '@/hooks/useIsFlutterWebView';
import { useIsMobile } from '@/hooks/use-mobile';
import { useResellerAccess } from '@/hooks/useResellerAccess';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  fetchDevices,
  fetchDrivers,
  fetchTrips,
  fetchSummary,
  fetchStops,
  fetchEvents,
  fetchRoute,
  fetchPositionsByIds,
  getCommandResultContent,
  type Device,
  type Driver,
  type Trip,
  type Summary,
  type Stop,
  type Event,
  type Position,
} from '@/lib/traccar-api';
import { fetchDeviceMerges } from '@/lib/reseller-api';
import { findExcursions, getTemperatureReadings, isOutOfRange, sortSensorKeys, summarizeTemperatures, thinForChart } from '@/lib/temperature';

type ReportType = 'trips' | 'events' | 'stops' | 'summary' | 'route' | 'raw' | 'driver' | 'speeding' | 'ranking' | 'activity' | 'events-chart' | 'driver-chart' | 'battery-voltage' | 'temperature' | 'idle' | 'hours';

const CHART_COLORS = ['#1a56db', '#7c3aed', '#059669', '#d97706', '#dc2626', '#0891b2', '#65a30d', '#db2777'];
const MAX_ENGINE_HOURS_GAP_MS = 10 * 60 * 1000;

const REPORT_TYPE_OPTIONS: { value: ReportType; labelKey: string }[] = [
  { value: 'trips', labelKey: 'reports.reportTypes.trips' },
  { value: 'events', labelKey: 'reports.reportTypes.events' },
  { value: 'stops', labelKey: 'reports.reportTypes.stops' },
  { value: 'summary', labelKey: 'reports.reportTypes.summary' },
  { value: 'hours', labelKey: 'reports.reportTypes.hours' },
  { value: 'route', labelKey: 'reports.reportTypes.route' },
  { value: 'raw', labelKey: 'reports.reportTypes.raw' },
  { value: 'driver', labelKey: 'reports.reportTypes.driver' },
  { value: 'speeding', labelKey: 'reports.reportTypes.speeding' },
  { value: 'idle', labelKey: 'reports.reportTypes.idle' },
  { value: 'ranking', labelKey: 'reports.reportTypes.ranking' },
  { value: 'activity', labelKey: 'reports.reportTypes.activity' },
  { value: 'events-chart', labelKey: 'reports.reportTypes.eventsChart' },
  { value: 'driver-chart', labelKey: 'reports.reportTypes.driverChart' },
  { value: 'battery-voltage', labelKey: 'reports.reportTypes.batteryVoltage' },
  { value: 'temperature', labelKey: 'reports.reportTypes.temperature' },
];

const getReportTypeLabelKey = (reportType: ReportType): string =>
  REPORT_TYPE_OPTIONS.find(option => option.value === reportType)?.labelKey ?? `reports.reportTypes.${reportType}`;

interface DriverSummary {
  driverUniqueId: string;
  driverName: string;
  trips: number;
  distance: number;
  duration: number;
  averageSpeed: number;
  maxSpeed: number;
}

interface SpeedingEvent {
  deviceName: string;
  driverName: string;
  startTime: string;
  endTime: string;
  duration: number;
  maxSpeed: number;
  avgSpeed: number;
  address: string;
  latitude: number;
  longitude: number;
  fixTime: string;
  startSpeed: number;
}

interface RankedDriver {
  rank: number;
  driverUniqueId: string;
  driverName: string;
  trips: number;
  distance: number;
  duration: number;
  averageSpeed: number; // knots
  maxSpeed: number;     // knots
  idleEvents: number;   // idle periods > SCORECARD_IDLE_MIN_MINUTES
  overspeed: number;
  harshBraking: number;
  harshAcceleration: number;
  harshCornering: number;
  severe: number;       // accident / at-fault crash alarms
  score: number;        // 0–100
}

interface HoursReportRow {
  id: string;
  deviceId: number;
  deviceName: string;
  startTime: string;
  endTime: string;
  engineHours: number;
  movingHours: number;
  idleHours: number;
  distance: number;
  averageSpeed: number; // knots
  maxSpeed: number;     // knots
  idleRatio: number;
}

// Driver scorecard ("carteira de pontos"): every driver starts the period with
// SCORECARD_START points and loses a fixed amount per infraction. Reset monthly by
// generating the report for each calendar month.
const SCORECARD_START = 100;
const SCORECARD_IDLE_MIN_MINUTES = 3;      // engine idling longer than this = one light infraction
const SCORECARD_PENALTY = {
  idle: 5,          // light:  idle > 3 min
  overspeed: 15,    // medium: overspeed alarm
  harshBraking: 15, // medium: hard-braking alarm
  severe: 50,       // severe: accident / at-fault crash
};

type ScorecardCounts = { idle: number; overspeed: number; harshBraking: number; severe: number };

const computeScorecard = (c: ScorecardCounts): number => {
  const penalty =
    c.idle * SCORECARD_PENALTY.idle +
    c.overspeed * SCORECARD_PENALTY.overspeed +
    c.harshBraking * SCORECARD_PENALTY.harshBraking +
    c.severe * SCORECARD_PENALTY.severe;
  return Math.max(0, SCORECARD_START - penalty);
};

const IDLE_RUN_SPEED = 2; // same low-speed threshold the Idle report uses
const IDLE_RUN_GAP_LIMIT = 5 * 60 * 1000;

// Counts idle periods (ignition on, not moving) longer than minMs per device, attributing
// each to a driver key via the position's own driverUniqueId or, failing that, `driverKeyForTime`.
const countIdleEventsByDriver = (
  positionsByDevice: Position[][],
  minMs: number,
  driverKeyForTime: (deviceId: number, timeMs: number) => string,
): Map<string, number> => {
  const counts = new Map<string, number>();
  const bump = (key: string) => counts.set(key, (counts.get(key) ?? 0) + 1);

  for (const positions of positionsByDevice) {
    if (positions.length === 0) continue;
    const sorted = [...positions].sort((a, b) => new Date(a.fixTime).getTime() - new Date(b.fixTime).getTime());
    let start: Position | null = null;
    let last: Position | null = null;
    let duration = 0;

    const flush = () => {
      if (start && duration >= minMs) {
        const explicit = (start.attributes?.driverUniqueId as string | undefined)?.trim();
        bump(explicit || driverKeyForTime(start.deviceId, new Date(start.fixTime).getTime()));
      }
      start = null;
      last = null;
      duration = 0;
    };

    for (const pos of sorted) {
      const idle = pos.speed < IDLE_RUN_SPEED && pos.attributes?.ignition === true;
      if (idle) {
        if (!start) {
          start = pos;
          duration = 0;
        } else if (last) {
          const gap = new Date(pos.fixTime).getTime() - new Date(last.fixTime).getTime();
          if (gap < IDLE_RUN_GAP_LIMIT) duration += gap;
        }
        last = pos;
      } else {
        flush();
      }
    }
    flush();
  }
  return counts;
};

function TwoLineAddress({ address }: { address: string | undefined | null }) {
  if (!address) return <span>—</span>;
  const comma = address.indexOf(',');
  if (comma === -1) return <span>{address}</span>;
  return (
    <span>
      {address.slice(0, comma)}
      <br />
      <span className="text-muted-foreground text-xs">{address.slice(comma + 1).trim()}</span>
    </span>
  );
}

function resolveDriverName(
  eventTime: number,
  devicePositions: Position[],
  firstPosDriverUniqueId: string | undefined,
  drivers: Driver[]
): string {
  let driverUniqueId = firstPosDriverUniqueId;

  if (!driverUniqueId?.trim()) {
    let ignitionOnTime: number | undefined;
    for (let i = devicePositions.length - 1; i >= 0; i--) {
      if (new Date(devicePositions[i].fixTime).getTime() > eventTime) continue;
      if (devicePositions[i].attributes?.ignition === true && !devicePositions[i - 1]?.attributes?.ignition) {
        ignitionOnTime = new Date(devicePositions[i].fixTime).getTime();
        break;
      }
    }

    let ignitionOffTime: number | undefined;
    for (const p of devicePositions) {
      if (new Date(p.fixTime).getTime() <= eventTime) continue;
      if (!p.attributes?.ignition) {
        ignitionOffTime = new Date(p.fixTime).getTime();
        break;
      }
    }

    if (ignitionOnTime !== undefined || ignitionOffTime !== undefined) {
      const found = devicePositions.find(p =>
        (ignitionOnTime === undefined || new Date(p.fixTime).getTime() >= ignitionOnTime) &&
        (ignitionOffTime === undefined || new Date(p.fixTime).getTime() <= ignitionOffTime) &&
        (p.attributes?.driverUniqueId as string | undefined)?.trim()
      );
      driverUniqueId = found?.attributes?.driverUniqueId as string | undefined;
    }
  }

  return drivers.find(d => d.uniqueId === driverUniqueId)?.name || driverUniqueId || '';
}

const calculateRouteEngineHours = (routePositions: Position[]): number => {
  const sortedPositions = [...routePositions]
    .sort((a, b) => new Date(a.fixTime).getTime() - new Date(b.fixTime).getTime());

  return sortedPositions.reduce((totalMs, currentPosition, index) => {
    if (index === 0) return totalMs;
    const previousPosition = sortedPositions[index - 1];
    if (previousPosition.attributes?.ignition !== true) return totalMs;

    const previousTime = new Date(previousPosition.fixTime).getTime();
    const currentTime = new Date(currentPosition.fixTime).getTime();
    if (!Number.isFinite(previousTime) || !Number.isFinite(currentTime)) return totalMs;

    const gap = currentTime - previousTime;
    if (gap < 0 || gap >= MAX_ENGINE_HOURS_GAP_MS) return totalMs;

    return totalMs + gap;
  }, 0);
};

const getNumericAttribute = (position: Position, ...keys: string[]): number | undefined => {
  for (const key of keys) {
    const value = position.attributes?.[key];
    const numeric = Number(value);
    if (value !== undefined && value !== null && Number.isFinite(numeric)) return numeric;
  }
  return undefined;
};

const getVehicleBatteryVoltage = (position: Position): number | undefined => {
  const power = getNumericAttribute(position, 'power');
  if (power !== undefined) return power;

  const rawPower = getNumericAttribute(position, 'io29');
  return rawPower !== undefined ? rawPower / 1000 : undefined;
};

const getPositionTemperature = (position: Position): number | undefined => {
  const readings = getTemperatureReadings(position);
  const first = sortSensorKeys(Object.keys(readings))[0];
  return first !== undefined ? readings[first] : getNumericAttribute(position, 'deviceTemp', 'coolantTemp');
};

const temperatureSensorLabel = (t: TFunction, sensor: string): string => {
  if (sensor === 'temperature') return t('reports.temperature.sensorGeneric');
  const ble = sensor.match(/^bleTemp(\d+)$/);
  if (ble) return t('reports.temperature.sensorBle', { n: ble[1] });
  return t('reports.temperature.sensor', { n: sensor.replace('temp', '') });
};

// Most recent temperature alerts listed per vehicle; the rest are summarized.
const MAX_TEMPERATURE_ALERT_ROWS = 50;

const parseRangeValue = (value: string): number | null => {
  if (value.trim() === '') return null;
  const numeric = Number(value.replace(',', '.'));
  return Number.isFinite(numeric) ? numeric : null;
};

const googleMapsUrl = (position: Position): string =>
  `https://www.google.com/maps?q=${position.latitude.toFixed(6)},${position.longitude.toFixed(6)}`;

const yesNo = (t: TFunction, value: unknown): string => {
  if (value === undefined || value === null) return '—';
  return t(value ? 'common.yes' : 'common.no');
};

// Optional columns for the route report, mirroring Traccar web's column picker.
type RouteExtraColumn = {
  id: string;
  labelKey: string;
  align?: 'right';
  cell: (position: Position, t: TFunction) => ReactNode;
  csv: (position: Position, t: TFunction) => string | number;
  // When set, the Excel export writes a real clickable hyperlink for this column,
  // using `csv(...)` as the visible label and this as the target URL.
  href?: (position: Position) => string;
};

const ROUTE_EXTRA_COLUMNS: RouteExtraColumn[] = [
  {
    id: 'googleMaps',
    labelKey: 'reports.columns.googleMaps',
    cell: (position, t) => (
      <a
        href={googleMapsUrl(position)}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-primary underline underline-offset-2"
      >
        <MapPin className="h-3.5 w-3.5 shrink-0" />
        {t('reports.columns.openInMaps')}
      </a>
    ),
    csv: (position, t) => t('reports.columns.openInMaps'),
    href: (position) => googleMapsUrl(position),
  },
  {
    id: 'temperature',
    labelKey: 'reports.columns.temperature',
    align: 'right',
    cell: (position) => {
      const value = getPositionTemperature(position);
      return value != null ? `${value.toFixed(1)} °C` : '—';
    },
    csv: (position) => {
      const value = getPositionTemperature(position);
      return value != null ? Number(value.toFixed(1)) : '';
    },
  },
  {
    id: 'battery',
    labelKey: 'reports.columns.battery',
    align: 'right',
    cell: (position) => {
      const value = getVehicleBatteryVoltage(position);
      return value != null ? `${value.toFixed(2)} V` : '—';
    },
    csv: (position) => {
      const value = getVehicleBatteryVoltage(position);
      return value != null ? Number(value.toFixed(2)) : '';
    },
  },
  {
    id: 'batteryLevel',
    labelKey: 'reports.columns.batteryLevel',
    align: 'right',
    cell: (position) => {
      const value = getNumericAttribute(position, 'batteryLevel');
      return value != null ? `${Math.round(value)}%` : '—';
    },
    csv: (position) => {
      const value = getNumericAttribute(position, 'batteryLevel');
      return value != null ? Math.round(value) : '';
    },
  },
  {
    id: 'ignition',
    labelKey: 'reports.columns.ignition',
    cell: (position, t) => yesNo(t, position.attributes?.ignition),
    csv: (position, t) => yesNo(t, position.attributes?.ignition),
  },
  {
    id: 'motion',
    labelKey: 'reports.columns.motion',
    cell: (position, t) => yesNo(t, position.attributes?.motion),
    csv: (position, t) => yesNo(t, position.attributes?.motion),
  },
  {
    id: 'altitude',
    labelKey: 'reports.columns.altitude',
    align: 'right',
    cell: (position) => `${Math.round(position.altitude)} m`,
    csv: (position) => Math.round(position.altitude),
  },
  {
    id: 'course',
    labelKey: 'reports.columns.course',
    align: 'right',
    cell: (position) => `${Math.round(position.course)}°`,
    csv: (position) => Math.round(position.course),
  },
  {
    id: 'distance',
    labelKey: 'reports.columns.distance',
    align: 'right',
    cell: (position) => {
      const value = getNumericAttribute(position, 'distance');
      return value != null ? `${value.toFixed(0)} m` : '—';
    },
    csv: (position) => {
      const value = getNumericAttribute(position, 'distance');
      return value != null ? Number(value.toFixed(0)) : '';
    },
  },
  {
    id: 'rpm',
    labelKey: 'reports.columns.rpm',
    align: 'right',
    cell: (position) => {
      const value = getNumericAttribute(position, 'rpm');
      return value != null ? String(Math.round(value)) : '—';
    },
    csv: (position) => {
      const value = getNumericAttribute(position, 'rpm');
      return value != null ? Math.round(value) : '';
    },
  },
  {
    id: 'fuel',
    labelKey: 'reports.columns.fuel',
    align: 'right',
    cell: (position) => {
      const value = getNumericAttribute(position, 'fuel');
      return value != null ? value.toFixed(1) : '—';
    },
    csv: (position) => {
      const value = getNumericAttribute(position, 'fuel');
      return value != null ? Number(value.toFixed(1)) : '';
    },
  },
  {
    id: 'valid',
    labelKey: 'reports.columns.valid',
    cell: (position, t) => t(position.valid ? 'common.yes' : 'common.no'),
    csv: (position, t) => t(position.valid ? 'common.yes' : 'common.no'),
  },
];

const DEFAULT_ROUTE_EXTRA_COLUMNS = ['googleMaps', 'temperature'];

const haversineMeters = (a: Position, b: Position): number => {
  const toRad = (value: number) => value * Math.PI / 180;
  const radius = 6371000;
  const lat1 = toRad(a.latitude);
  const lat2 = toRad(b.latitude);
  const deltaLat = toRad(b.latitude - a.latitude);
  const deltaLon = toRad(b.longitude - a.longitude);
  const h = Math.sin(deltaLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLon / 2) ** 2;
  return 2 * radius * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
};

const calculateDetailedHoursForSegment = (
  routePositions: Position[],
  deviceName: string,
  segmentIndex: number,
): HoursReportRow | null => {
  if (routePositions.length < 2) return null;

  const engineHours = calculateRouteEngineHours(routePositions);
  let movingHours = 0;
  let idleHours = 0;
  let distance = 0;
  let speedTime = 0;
  let weightedSpeed = 0;
  let maxSpeed = 0;

  for (let i = 1; i < routePositions.length; i++) {
    const previous = routePositions[i - 1];
    const current = routePositions[i];
    const previousTime = new Date(previous.fixTime).getTime();
    const currentTime = new Date(current.fixTime).getTime();
    if (!Number.isFinite(previousTime) || !Number.isFinite(currentTime)) continue;

    const gap = currentTime - previousTime;
    if (gap < 0 || gap >= MAX_ENGINE_HOURS_GAP_MS) continue;

    const previousIgnition = previous.attributes?.ignition === true;
    const previousSpeed = Number(previous.speed) || 0;

    if (previousIgnition) {
      weightedSpeed += previousSpeed * gap;
      speedTime += gap;
      if (previousSpeed >= 2) movingHours += gap;
      else idleHours += gap;
    }

    maxSpeed = Math.max(maxSpeed, previousSpeed, Number(current.speed) || 0);

    const previousOdometer = getNumericAttribute(previous, 'odometer', 'obdOdometer', 'totalDistance');
    const currentOdometer = getNumericAttribute(current, 'odometer', 'obdOdometer', 'totalDistance');
    const odometerDelta = previousOdometer !== undefined && currentOdometer !== undefined
      ? currentOdometer - previousOdometer
      : NaN;
    distance += Number.isFinite(odometerDelta) && odometerDelta >= 0 && odometerDelta < 1000000
      ? odometerDelta
      : haversineMeters(previous, current);
  }

  if (engineHours <= 0) return null;

  const first = routePositions[0];
  const last = routePositions[routePositions.length - 1];

  return {
    id: `${first.deviceId}-${segmentIndex}-${first.id}-${last.id}`,
    deviceId: first.deviceId,
    deviceName,
    startTime: first.fixTime,
    endTime: last.fixTime,
    engineHours,
    movingHours,
    idleHours,
    distance,
    averageSpeed: speedTime > 0 ? weightedSpeed / speedTime : 0,
    maxSpeed,
    idleRatio: engineHours > 0 ? idleHours / engineHours : 0,
  };
};

const calculateDetailedHours = (
  routePositions: Position[],
  deviceName: string,
): HoursReportRow[] => {
  const sortedPositions = [...routePositions]
    .sort((a, b) => new Date(a.fixTime).getTime() - new Date(b.fixTime).getTime());

  const rows: HoursReportRow[] = [];
  let currentSegment: Position[] = [];

  for (const position of sortedPositions) {
    const ignitionOn = position.attributes?.ignition === true;

    if (ignitionOn) {
      currentSegment.push(position);
      continue;
    }

    if (currentSegment.length > 0) {
      currentSegment.push(position);
      const row = calculateDetailedHoursForSegment(currentSegment, deviceName, rows.length + 1);
      if (row) rows.push(row);
      currentSegment = [];
    }
  }

  if (currentSegment.length > 1) {
    const row = calculateDetailedHoursForSegment(currentSegment, deviceName, rows.length + 1);
    if (row) rows.push(row);
  }

  return rows;
};

const Reports = () => {
  const { t } = useTranslation();
  const dateFnsLocale = useDateLocale();
  const { isAuthenticated, user } = useTraccarSession();
  const { title: brandTitle, favicon: brandLogo } = useBranding();
  const isFlutter = useIsFlutterWebView();
  const isMobile = useIsMobile();
  const { isReseller } = useResellerAccess();
  const shouldApplyMerges = !isReseller && !user?.administrator;
  const [searchParams] = useSearchParams();

  const [devices, setDevices] = useState<Device[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [reportType, setReportType] = useState<ReportType>(() => (searchParams.get('type') as ReportType) || 'trips');
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>(() => searchParams.get('deviceId') || 'all');
  const [devicePickerOpen, setDevicePickerOpen] = useState(false);
  const [selectedDriverId, setSelectedDriverId] = useState<string>('all');
  const [speedLimit, setSpeedLimit] = useState<number>(90);
  const [minIdleTime, setMinIdleTime] = useState<number>(10);
  // Allowed temperature range (°C) for the temperature report; empty = no limit.
  const [tempRangeMin, setTempRangeMin] = useState(() => searchParams.get('tempMin') ?? '');
  const [tempRangeMax, setTempRangeMax] = useState(() => searchParams.get('tempMax') ?? '');
  const [routeExtraColumns, setRouteExtraColumns] = useState<Set<string>>(() => new Set(DEFAULT_ROUTE_EXTRA_COLUMNS));
  const [routeColumnsOpen, setRouteColumnsOpen] = useState(false);
  const [fromTime, setFromTime] = useState('00:00');
  const [toTime, setToTime] = useState('23:59');
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() => {
    const dateParam = searchParams.get('date');
    if (dateParam) {
      const d = new Date(dateParam);
      return { from: startOfDay(d), to: endOfDay(d) };
    }
    return { from: startOfDay(subDays(new Date(), 3)), to: endOfDay(new Date()) };
  });

  // Report data states
  const [trips, setTrips] = useState<Trip[]>([]);
  const [summaries, setSummaries] = useState<Summary[]>([]);
  const [stops, setStops] = useState<Stop[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [eventTypeFilter, setEventTypeFilter] = useState<Set<string>>(new Set());
  const [eventPositions, setEventPositions] = useState<Map<number, Position>>(new Map());
  const [positions, setPositions] = useState<Position[]>([]);
  const [rawPage, setRawPage] = useState(0);
  const [routePage, setRoutePage] = useState(0);
  const POSITIONS_PAGE_SIZE = 500;
  const [driverTrips, setDriverTrips] = useState<DriverSummary[]>([]);
  const [speedingEvents, setSpeedingEvents] = useState<SpeedingEvent[]>([]);
  const [rankingData, setRankingData] = useState<RankedDriver[]>([]);
  const [hoursReport, setHoursReport] = useState<HoursReportRow[]>([]);
  const [hasGenerated, setHasGenerated] = useState(false);

  const { data: deviceMerges = [] } = useQuery({
    queryKey: ['device-merges'],
    queryFn: fetchDeviceMerges,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
  const mergeSecondaryIds = useMemo(() => {
    const set = new Set<number>();
    if (shouldApplyMerges) deviceMerges.forEach(m => set.add(m.secondary_device_id));
    return set;
  }, [deviceMerges, shouldApplyMerges]);
  const mergePrimaryOf = useMemo(() => {
    const map = new Map<number, number>();
    if (shouldApplyMerges) deviceMerges.forEach(m => map.set(m.secondary_device_id, m.primary_device_id));
    return map;
  }, [deviceMerges, shouldApplyMerges]);
  const mergeSecondaryOf = useMemo(() => {
    const map = new Map<number, number[]>();
    if (shouldApplyMerges) deviceMerges.forEach(m => {
      if (!map.has(m.primary_device_id)) map.set(m.primary_device_id, []);
      map.get(m.primary_device_id)!.push(m.secondary_device_id);
    });
    return map;
  }, [deviceMerges, shouldApplyMerges]);
  const visibleDevices = useMemo(() =>
    shouldApplyMerges ? devices.filter(d => !mergeSecondaryIds.has(d.id)) : devices,
    [devices, shouldApplyMerges, mergeSecondaryIds]
  );

  const activeRouteExtraColumns = useMemo(
    () => ROUTE_EXTRA_COLUMNS.filter(col => routeExtraColumns.has(col.id)),
    [routeExtraColumns]
  );

  // If the auto-selected device turns out to be a secondary, redirect to its primary.
  useEffect(() => {
    if (!mergeSecondaryIds.size) return;
    setSelectedDeviceId(prev => {
      if (prev === 'all') return prev;
      const id = parseInt(prev, 10);
      if (!mergeSecondaryIds.has(id)) return prev;
      return mergePrimaryOf.get(id)?.toString() ?? 'all';
    });
  }, [mergeSecondaryIds, mergePrimaryOf]);

  const deviceIdParam = searchParams.get('deviceId');
  const loadInitialData = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      setIsLoading(true);
      const [devicesData, driversData] = await Promise.all([fetchDevices(), fetchDrivers()]);
      setDevices(devicesData);
      setDrivers(driversData);
      if (devicesData.length > 0) {
        const valid = deviceIdParam && devicesData.some(d => d.id.toString() === deviceIdParam);
        setSelectedDeviceId(valid ? deviceIdParam : devicesData[0].id.toString());
      }
      setHasLoaded(true);
    } catch (error) {
      console.error('Failed to load initial data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, deviceIdParam]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const formatHours = useCallback((ms: number) => {
    const hours = Math.floor(ms / 3600000);
    const minutes = Math.floor((ms % 3600000) / 60000);
    return formatDuration({ hours, minutes }, { format: ['hours', 'minutes'], zero: false, locale: dateFnsLocale }) || '0h';
  }, [dateFnsLocale]);

  const clearResults = () => {
    setTrips([]);
    setSummaries([]);
    setStops([]);
    setEvents([]);
    setEventTypeFilter(new Set());
    setEventPositions(new Map());
    setPositions([]);
    setDriverTrips([]);
    setSpeedingEvents([]);
    setRankingData([]);
    setHoursReport([]);
  };

  const generateReport = useCallback(async () => {
    if (!dateRange?.from) return;

    try {
      setIsGenerating(true);
      clearResults();
      const from = dateRange.from.toISOString();
      const to = (dateRange.to ?? endOfDay(dateRange.from)).toISOString();
      const isAll = selectedDeviceId === 'all';
      const deviceId = isAll ? NaN : parseInt(selectedDeviceId, 10);
      const deviceIds = isAll ? devices.map(d => d.id) : [
        deviceId,
        ...(shouldApplyMerges ? (mergeSecondaryOf.get(deviceId) ?? []) : []),
      ];

      const resolvedName = (id: number): string => {
        if (shouldApplyMerges) {
          const primaryId = mergePrimaryOf.get(id);
          if (primaryId !== undefined) {
            const merge = deviceMerges.find(m => m.secondary_device_id === id);
            return merge?.display_name || devices.find(d => d.id === primaryId)?.name || `Device ${primaryId}`;
          }
        }
        return devices.find(d => d.id === id)?.name || `Device ${id}`;
      };

      switch (reportType) {
        case 'trips': {
          const arrays = await Promise.all(deviceIds.map(id => fetchTrips(id, from, to).catch(() => [] as Trip[])));
          const allTrips = arrays.flat();
          if (shouldApplyMerges) {
            for (const trip of allTrips) {
              if (mergePrimaryOf.has(trip.deviceId)) trip.deviceName = resolvedName(trip.deviceId);
            }
          }

          for (const trip of allTrips) {
            if (!trip.driverName?.trim() && trip.driverUniqueId?.trim()) {
              trip.driverName = trip.driverUniqueId;
            }
          }

          const missingDriverDeviceIds = [...new Set(
            allTrips.filter(t => !t.driverUniqueId?.trim()).map(t => t.deviceId)
          )];

          if (missingDriverDeviceIds.length > 0) {
            const posArrays = await Promise.all(
              missingDriverDeviceIds.map(id => fetchRoute(id, from, to).catch(() => [] as Position[]))
            );
            const positionsByDevice = new Map<number, Position[]>();
            for (let i = 0; i < missingDriverDeviceIds.length; i++) {
              const sorted = [...posArrays[i]].sort((a, b) => new Date(a.fixTime).getTime() - new Date(b.fixTime).getTime());
              positionsByDevice.set(missingDriverDeviceIds[i], sorted);
            }

            for (const trip of allTrips) {
              if (trip.driverUniqueId?.trim()) continue;
              const devPositions = positionsByDevice.get(trip.deviceId);
              if (!devPositions?.length) continue;
              trip.driverName = resolveDriverName(
                new Date(trip.startTime).getTime(),
                devPositions,
                undefined,
                drivers
              ) || undefined;
            }
          }

          setTrips(allTrips);
          break;
        }
        case 'summary': {
          const [summaryData, routeArrays] = await Promise.all([
            fetchSummary(deviceIds, from, to),
            Promise.all(deviceIds.map(id => fetchRoute(id, from, to).catch(() => [] as Position[]))),
          ]);
          const routeEngineHoursByDevice = new Map<number, number>();
          deviceIds.forEach((id, index) => {
            routeEngineHoursByDevice.set(id, calculateRouteEngineHours(routeArrays[index] ?? []));
          });
          const data = summaryData.map(s => ({
            ...s,
            engineHours: routeEngineHoursByDevice.get(s.deviceId) ?? 0,
          }));

          if (shouldApplyMerges) {
            const agg = new Map<number, Summary>();
            for (const s of data) {
              const primaryId = mergePrimaryOf.get(s.deviceId) ?? s.deviceId;
              if (agg.has(primaryId)) {
                const e = agg.get(primaryId)!;
                const totalDist = e.distance + s.distance;
                agg.set(primaryId, {
                  ...e,
                  distance: totalDist,
                  engineHours: e.engineHours + s.engineHours,
                  spentFuel: e.spentFuel + s.spentFuel,
                  maxSpeed: Math.max(e.maxSpeed, s.maxSpeed),
                  averageSpeed: totalDist > 0
                    ? (e.averageSpeed * e.distance + s.averageSpeed * s.distance) / totalDist
                    : 0,
                });
              } else {
                agg.set(primaryId, { ...s, deviceId: primaryId, deviceName: resolvedName(s.deviceId) });
              }
            }
            setSummaries(Array.from(agg.values()));
          } else {
            setSummaries(data);
          }
          break;
        }
        case 'hours': {
          const routeArrays = await Promise.all(deviceIds.map(id => fetchRoute(id, from, to).catch(() => [] as Position[])));
          const rows = routeArrays
            .flatMap((positions, index) => calculateDetailedHours(positions, resolvedName(deviceIds[index])))
            .map(row => shouldApplyMerges && mergePrimaryOf.has(row.deviceId)
              ? { ...row, deviceName: resolvedName(row.deviceId) }
              : row)
            .sort((a, b) => a.deviceName.localeCompare(b.deviceName) || new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

          setHoursReport(rows);
          break;
        }
        case 'stops': {
          const arrays = await Promise.all(deviceIds.map(id => fetchStops(id, from, to).catch(() => [] as Stop[])));
          const allStops = arrays.flat();
          if (shouldApplyMerges) {
            for (const stop of allStops) {
              if (mergePrimaryOf.has(stop.deviceId)) stop.deviceName = resolvedName(stop.deviceId);
            }
          }
          setStops(allStops);
          break;
        }
        case 'idle': {
          const arrays = await Promise.all(deviceIds.map(id => fetchRoute(id, from, to).catch(() => [] as Position[])));
          const minMs = minIdleTime * 60 * 1000;
          const FIVE_MIN = 5 * 60 * 1000;
          const IDLE_SPEED = 2; // km/h
          const idleStops: Stop[] = [];

          for (const positions of arrays) {
            if (positions.length === 0) continue;
            const sorted = [...positions].sort((a, b) => new Date(a.fixTime).getTime() - new Date(b.fixTime).getTime());
            const deviceId = sorted[0].deviceId;
            const deviceName = resolvedName(deviceId);

            let idleStart: Position | null = null;
            let lastIdlePos: Position | null = null;
            let idleDuration = 0;

            for (const pos of sorted) {
              const isIdle = pos.speed < IDLE_SPEED && pos.attributes?.ignition === true;

              if (isIdle) {
                if (idleStart === null) {
                  idleStart = pos;
                  idleDuration = 0;
                } else if (lastIdlePos) {
                  const gap = new Date(pos.fixTime).getTime() - new Date(lastIdlePos.fixTime).getTime();
                  if (gap < FIVE_MIN) {
                    idleDuration += gap;
                  }
                }
                lastIdlePos = pos;
              } else {
                if (idleStart !== null && idleDuration >= minMs) {
                  const driverName = resolveDriverName(
                    new Date(idleStart.fixTime).getTime(),
                    sorted,
                    idleStart.attributes?.driverUniqueId as string | undefined,
                    drivers
                  );
                  idleStops.push({
                    deviceId,
                    deviceName,
                    driverName,
                    duration: new Date(lastIdlePos!.fixTime).getTime() - new Date(idleStart.fixTime).getTime(),
                    startTime: idleStart.fixTime,
                    endTime: lastIdlePos!.fixTime,
                    address: idleStart.address || '',
                    latitude: idleStart.latitude,
                    longitude: idleStart.longitude,
                    engineHours: idleDuration,
                  });
                }
                idleStart = null;
                lastIdlePos = null;
                idleDuration = 0;
              }
            }

            // Handle trailing idle period
            if (idleStart !== null && lastIdlePos && idleDuration >= minMs) {
              const driverName = resolveDriverName(
                new Date(idleStart.fixTime).getTime(),
                sorted,
                idleStart.attributes?.driverUniqueId as string | undefined,
                drivers
              );
              idleStops.push({
                deviceId,
                deviceName,
                driverName,
                duration: new Date(lastIdlePos.fixTime).getTime() - new Date(idleStart.fixTime).getTime(),
                startTime: idleStart.fixTime,
                endTime: lastIdlePos.fixTime,
                address: idleStart.address || '',
                latitude: idleStart.latitude,
                longitude: idleStart.longitude,
                engineHours: idleDuration,
              });
            }
          }

          setStops(idleStops);
          break;
        }
        case 'events': {
          const arrays = await Promise.all(deviceIds.map(id => fetchEvents(id, from, to).catch(() => [] as Event[])));
          const evs = arrays.flat();
          setEvents(evs);
          setEventTypeFilter(new Set());
          const posIds = [...new Set(evs.map(ev => ev.positionId).filter(Boolean))];
          const evPositions = await fetchPositionsByIds(posIds).catch(() => [] as Position[]);
          setEventPositions(new Map(evPositions.map(p => [p.id, p])));
          break;
        }
        case 'route': {
          const arrays = await Promise.all(deviceIds.map(id => fetchRoute(id, from, to).catch(() => [] as Position[])));
          setPositions(arrays.flat());
          setRoutePage(0);
          break;
        }
        case 'raw': {
          const arrays = await Promise.all(deviceIds.map(id => fetchRoute(id, from, to).catch(() => [] as Position[])));
          setPositions(arrays.flat());
          setRawPage(0);
          break;
        }
        case 'battery-voltage': {
          const arrays = await Promise.all(deviceIds.map(id => fetchRoute(id, from, to).catch(() => [] as Position[])));
          setPositions(arrays.flat().filter(position => {
            const voltage = getVehicleBatteryVoltage(position);
            return voltage !== undefined && voltage > 0;
          }));
          break;
        }
        case 'temperature': {
          const arrays = await Promise.all(deviceIds.map(id => fetchRoute(id, from, to).catch(() => [] as Position[])));
          setPositions(arrays.flat().filter(position => Object.keys(getTemperatureReadings(position)).length > 0));
          break;
        }
        case 'driver': {
          const allTripsArrays = await Promise.all(
            devices.map(d => fetchTrips(d.id, from, to).catch(() => [] as Trip[]))
          );
          const allTrips = allTripsArrays.flat();
          const filtered = selectedDriverId === 'all'
            ? allTrips
            : allTrips.filter(t => t.driverUniqueId === selectedDriverId);

          const accumulated = new Map<string, { driverName: string; trips: number; distance: number; duration: number; speedSum: number; maxSpeed: number }>();
          for (const trip of filtered) {
            const key = trip.driverUniqueId || '';
            const driverName = trip.driverName ||
              drivers.find(d => d.uniqueId === trip.driverUniqueId)?.name ||
              (trip.driverUniqueId ? `Unknown (${trip.driverUniqueId})` : t('reports.noDriver'));
            if (!accumulated.has(key)) {
              accumulated.set(key, { driverName, trips: 0, distance: 0, duration: 0, speedSum: 0, maxSpeed: 0 });
            }
            const entry = accumulated.get(key)!;
            entry.trips++;
            entry.distance += trip.distance;
            entry.duration += trip.duration;
            entry.speedSum += trip.averageSpeed;
            entry.maxSpeed = Math.max(entry.maxSpeed, trip.maxSpeed);
          }

          setDriverTrips(Array.from(accumulated.entries()).map(([driverUniqueId, e]) => ({
            driverUniqueId,
            driverName: e.driverName,
            trips: e.trips,
            distance: e.distance,
            duration: e.duration,
            averageSpeed: e.trips > 0 ? e.speedSum / e.trips : 0,
            maxSpeed: e.maxSpeed,
          })));
          break;
        }
        case 'speeding': {
          const arrays = await Promise.all(deviceIds.map(id => fetchRoute(id, from, to).catch(() => [] as Position[])));
          const data = arrays.flat();
          const sorted = [...data].sort((a, b) => new Date(a.fixTime).getTime() - new Date(b.fixTime).getTime());
          const limitKnots = speedLimit / 1.852;
          const result: SpeedingEvent[] = [];
          let group: Position[] = [];

          const finalizeGroup = () => {
            if (group.length === 0) return;
            const startTime = group[0].fixTime;
            const endTime = group[group.length - 1].fixTime;
            const duration = new Date(endTime).getTime() - new Date(startTime).getTime();
            const maxSpeed = Math.max(...group.map(p => p.speed));
            const avgSpeed = group.reduce((sum, p) => sum + p.speed, 0) / group.length;
            const deviceName = resolvedName(group[0].deviceId);
            const { latitude, longitude, fixTime, speed: startSpeed } = group[0];
            const deviceId = group[0].deviceId;
            const devPositions = sorted.filter(p => p.deviceId === deviceId);
            const driverName = resolveDriverName(
              new Date(group[0].fixTime).getTime(),
              devPositions,
              group[0].attributes?.driverUniqueId as string | undefined,
              drivers
            );
            result.push({ deviceName, driverName, startTime, endTime, duration, maxSpeed, avgSpeed, address: group[0].address || '', latitude, longitude, fixTime, startSpeed });
            group = [];
          };

          for (const pos of sorted) {
            if (pos.speed > limitKnots) {
              group.push(pos);
            } else {
              finalizeGroup();
            }
          }
          finalizeGroup();
          result.sort((a, b) => a.deviceName.localeCompare(b.deviceName) || new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
          setSpeedingEvents(result);
          break;
        }
        case 'ranking': {
          // Weekly/monthly driver scorecard. Routes are fetched too (small fleets) so idle
          // periods can be detected from raw positions the same way the Idle report does.
          const [allTripsArrays, allEventsArrays, allRouteArrays] = await Promise.all([
            Promise.all(devices.map(d => fetchTrips(d.id, from, to).catch(() => [] as Trip[]))),
            Promise.all(devices.map(d => fetchEvents(d.id, from, to).catch(() => [] as Event[]))),
            Promise.all(devices.map(d => fetchRoute(d.id, from, to).catch(() => [] as Position[]))),
          ]);
          const allTrips = allTripsArrays.flat();
          const allEvents = allEventsArrays.flat();

          // Build trip index for event/idle attribution: deviceId → trips sorted by start time
          const tripsByDevice = new Map<number, Trip[]>();
          for (const trip of allTrips) {
            if (!tripsByDevice.has(trip.deviceId)) tripsByDevice.set(trip.deviceId, []);
            tripsByDevice.get(trip.deviceId)!.push(trip);
          }
          for (const list of tripsByDevice.values()) {
            list.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
          }

          // Resolve which driver was at the wheel of a device at a given instant: the trip that
          // contains it, otherwise the trip with the smallest time gap.
          const driverKeyForTime = (deviceId: number, timeMs: number): string => {
            const list = tripsByDevice.get(deviceId) || [];
            let best: Trip | undefined;
            let bestGap = Infinity;
            for (const tr of list) {
              const s = new Date(tr.startTime).getTime();
              const e = new Date(tr.endTime).getTime();
              if (timeMs >= s && timeMs <= e) return tr.driverUniqueId || '';
              const gap = timeMs < s ? s - timeMs : timeMs - e;
              if (gap < bestGap) { bestGap = gap; best = tr; }
            }
            return best?.driverUniqueId || '';
          };

          // Tally infractions per driver from alarm events + detected idle periods.
          type InfractionCounts = ScorecardCounts & { harshAcceleration: number; harshCornering: number };
          const infractions = new Map<string, InfractionCounts>();
          const ensure = (key: string): InfractionCounts => {
            let entry = infractions.get(key);
            if (!entry) {
              entry = { idle: 0, overspeed: 0, harshBraking: 0, harshAcceleration: 0, harshCornering: 0, severe: 0 };
              infractions.set(key, entry);
            }
            return entry;
          };

          for (const ev of allEvents) {
            if (ev.type !== 'alarm') continue;
            const alarm = ev.attributes?.alarm as string | undefined;
            if (!alarm) continue;
            const entry = ensure(driverKeyForTime(ev.deviceId, new Date(ev.eventTime).getTime()));
            if (alarm === 'hardBraking') entry.harshBraking++;
            else if (alarm === 'hardAcceleration') entry.harshAcceleration++;
            else if (alarm === 'hardCornering') entry.harshCornering++;
            else if (alarm === 'overspeed') entry.overspeed++;
            else if (alarm === 'accident') entry.severe++;
          }

          const idleCounts = countIdleEventsByDriver(
            allRouteArrays,
            SCORECARD_IDLE_MIN_MINUTES * 60 * 1000,
            driverKeyForTime,
          );
          for (const [key, n] of idleCounts) ensure(key).idle += n;

          const accumulated = new Map<string, { driverName: string; trips: number; distance: number; duration: number; speedSum: number; maxSpeed: number }>();
          for (const trip of allTrips) {
            const key = trip.driverUniqueId || '';
            const driverName = trip.driverName ||
              drivers.find(d => d.uniqueId === trip.driverUniqueId)?.name ||
              (trip.driverUniqueId ? `Unknown (${trip.driverUniqueId})` : t('reports.noDriver'));
            if (!accumulated.has(key)) {
              accumulated.set(key, { driverName, trips: 0, distance: 0, duration: 0, speedSum: 0, maxSpeed: 0 });
            }
            const entry = accumulated.get(key)!;
            entry.trips++;
            entry.distance += trip.distance;
            entry.duration += trip.duration;
            entry.speedSum += trip.averageSpeed;
            entry.maxSpeed = Math.max(entry.maxSpeed, trip.maxSpeed);
          }

          const ranked = Array.from(accumulated.entries())
            .map(([driverUniqueId, e]) => {
              const averageSpeed = e.trips > 0 ? e.speedSum / e.trips : 0;
              const c = infractions.get(driverUniqueId) ||
                { idle: 0, overspeed: 0, harshBraking: 0, harshAcceleration: 0, harshCornering: 0, severe: 0 };
              return {
                rank: 0,
                driverUniqueId,
                driverName: e.driverName,
                trips: e.trips,
                distance: e.distance,
                duration: e.duration,
                averageSpeed,
                maxSpeed: e.maxSpeed,
                idleEvents: c.idle,
                overspeed: c.overspeed,
                harshBraking: c.harshBraking,
                harshAcceleration: c.harshAcceleration,
                harshCornering: c.harshCornering,
                severe: c.severe,
                score: computeScorecard(c),
              };
            })
            .sort((a, b) => b.score - a.score || a.driverName.localeCompare(b.driverName))
            .map((d, i) => ({ ...d, rank: i + 1 }));

          setRankingData(ranked);
          break;
        }
        case 'activity': {
          const arrays = await Promise.all(deviceIds.map(id => fetchTrips(id, from, to).catch(() => [] as Trip[])));
          setTrips(arrays.flat());
          break;
        }
        case 'events-chart': {
          const arrays = await Promise.all(deviceIds.map(id => fetchEvents(id, from, to).catch(() => [] as Event[])));
          setEvents(arrays.flat());
          break;
        }
        case 'driver-chart': {
          const allTripsArrays = await Promise.all(
            devices.map(d => fetchTrips(d.id, from, to).catch(() => [] as Trip[]))
          );
          const allTrips = allTripsArrays.flat();
          const accumulated = new Map<string, { driverName: string; trips: number; distance: number; speedSum: number; maxSpeed: number }>();
          for (const trip of allTrips) {
            const key = trip.driverUniqueId || '';
            const driverName = trip.driverName ||
              drivers.find(d => d.uniqueId === trip.driverUniqueId)?.name ||
              (trip.driverUniqueId ? `Unknown (${trip.driverUniqueId})` : t('reports.noDriver'));
            if (!accumulated.has(key)) accumulated.set(key, { driverName, trips: 0, distance: 0, speedSum: 0, maxSpeed: 0 });
            const e = accumulated.get(key)!;
            e.trips++;
            e.distance += trip.distance;
            e.speedSum += trip.averageSpeed;
            e.maxSpeed = Math.max(e.maxSpeed, trip.maxSpeed);
          }
          setDriverTrips(Array.from(accumulated.entries()).map(([driverUniqueId, e]) => ({
            driverUniqueId,
            driverName: e.driverName,
            trips: e.trips,
            distance: e.distance,
            duration: 0,
            averageSpeed: e.trips > 0 ? e.speedSum / e.trips : 0,
            maxSpeed: e.maxSpeed,
          })));
          break;
        }
      }
      setHasGenerated(true);
    } catch (error) {
      console.error('Failed to generate report:', error);
    } finally {
      setIsGenerating(false);
    }
  }, [dateRange, reportType, selectedDeviceId, selectedDriverId, speedLimit, minIdleTime, devices, drivers, t, shouldApplyMerges, mergePrimaryOf, mergeSecondaryOf, deviceMerges]);

  const getDeviceName = useCallback((deviceId: number) => {
    if (shouldApplyMerges) {
      const primaryId = mergePrimaryOf.get(deviceId);
      if (primaryId !== undefined) {
        const merge = deviceMerges.find(m => m.secondary_device_id === deviceId);
        return merge?.display_name || devices.find(d => d.id === primaryId)?.name || `Device ${primaryId}`;
      }
    }
    return devices.find(d => d.id === deviceId)?.name || `Device ${deviceId}`;
  }, [shouldApplyMerges, mergePrimaryOf, deviceMerges, devices]);

  const hasData = () => {
    switch (reportType) {
      case 'trips': return trips.length > 0;
      case 'summary': return summaries.length > 0;
      case 'hours': return hoursReport.length > 0;
      case 'stops': return stops.length > 0;
      case 'idle': return stops.length > 0;
      case 'events': return events.length > 0;
      case 'route': return positions.length > 0;
      case 'raw': return positions.length > 0;
      case 'battery-voltage': return positions.length > 0;
      case 'temperature': return positions.length > 0;
      case 'driver': return driverTrips.length > 0;
      case 'speeding': return speedingEvents.length > 0;
      case 'ranking': return rankingData.length > 0;
      case 'activity': return trips.length > 0;
      case 'events-chart': return events.length > 0;
      case 'driver-chart': return driverTrips.length > 0;
    }
  };

  const exportToExcel = useCallback(() => {
    const wb = XLSX.utils.book_new();
    let ws: XLSX.WorkSheet;

    switch (reportType) {
      case 'trips': {
        const data = trips.map(trip => ({
          [t('devices.name')]: trip.deviceName,
          [t('reports.driver')]: trip.driverName || '',
          [t('history.start')]: format(new Date(trip.startTime), 'yyyy-MM-dd HH:mm'),
          [t('history.end')]: format(new Date(trip.endTime), 'yyyy-MM-dd HH:mm'),
          [t('history.distance')]: `${(trip.distance / 1000).toFixed(1)} km`,
          [t('history.duration')]: formatHours(trip.duration),
          [t('history.avgSpeed')]: `${Math.round(trip.averageSpeed * 1.852)} km/h`,
          [t('history.maxSpeed')]: `${Math.round(trip.maxSpeed * 1.852)} km/h`,
          [t('history.start') + ' ' + t('devices.address')]: trip.startAddress,
          [t('history.end') + ' ' + t('devices.address')]: trip.endAddress,
        }));
        ws = XLSX.utils.json_to_sheet(data);
        break;
      }
      case 'summary': {
        const data = summaries.map(s => ({
          [t('devices.name')]: s.deviceName,
          [t('history.distance')]: `${(s.distance / 1000).toFixed(1)} km`,
          [t('history.avgSpeed')]: `${Math.round(s.averageSpeed * 1.852)} km/h`,
          [t('history.maxSpeed')]: `${Math.round(s.maxSpeed * 1.852)} km/h`,
          [t('history.engineHours')]: formatHours(s.engineHours),
          [t('history.fuel')]: `${s.spentFuel.toFixed(1)} L`,
        }));
        ws = XLSX.utils.json_to_sheet(data);
        break;
      }
      case 'hours': {
        const data = hoursReport.map(row => ({
          [t('devices.name')]: row.deviceName,
          [t('history.start')]: format(new Date(row.startTime), 'yyyy-MM-dd HH:mm'),
          [t('history.end')]: format(new Date(row.endTime), 'yyyy-MM-dd HH:mm'),
          [t('history.engineHours')]: formatHours(row.engineHours),
          [t('reports.movingHours')]: formatHours(row.movingHours),
          [t('reports.idleTime')]: formatHours(row.idleHours),
          [t('history.distance')]: `${(row.distance / 1000).toFixed(1)} km`,
          [t('history.avgSpeed')]: `${Math.round(row.averageSpeed * 1.852)} km/h`,
          [t('history.maxSpeed')]: `${Math.round(row.maxSpeed * 1.852)} km/h`,
          [t('reports.idlePercent')]: `${Math.round(row.idleRatio * 100)}%`,
        }));
        ws = XLSX.utils.json_to_sheet(data);
        break;
      }
      case 'stops': {
        const data = stops.map(stop => ({
          [t('devices.name')]: stop.deviceName,
          [t('history.start')]: format(new Date(stop.startTime), 'yyyy-MM-dd HH:mm'),
          [t('history.end')]: format(new Date(stop.endTime), 'yyyy-MM-dd HH:mm'),
          [t('history.duration')]: formatHours(stop.duration),
          [t('devices.address')]: stop.address,
          [t('history.engineHours')]: formatHours(stop.engineHours),
        }));
        ws = XLSX.utils.json_to_sheet(data);
        break;
      }
      case 'idle': {
        const data = stops.map(stop => ({
          [t('devices.name')]: stop.deviceName,
          [t('reports.driver')]: stop.driverName || '',
          [t('history.start')]: format(new Date(stop.startTime), 'yyyy-MM-dd HH:mm'),
          [t('history.end')]: format(new Date(stop.endTime), 'yyyy-MM-dd HH:mm'),
          [t('reports.idleTime')]: formatHours(stop.engineHours),
          [t('history.duration')]: formatHours(stop.duration),
          [t('devices.address')]: stop.address,
        }));
        ws = XLSX.utils.json_to_sheet(data);
        break;
      }
      case 'events': {
        const data = events.map(ev => {
          const driverUniqueId = ev.attributes?.driverUniqueId as string | undefined;
          const driverName = driverUniqueId ? (drivers.find(d => d.uniqueId === driverUniqueId)?.name || driverUniqueId) : '';
          return {
            [t('devices.name')]: devices.find(d => d.id === ev.deviceId)?.name || `Device ${ev.deviceId}`,
            [t('reports.type')]: ev.type === 'alarm' && ev.attributes?.alarm
              ? `${t(`notifications.events.${ev.type}`, ev.type)} (${t(`notifications.alarms.${ev.attributes.alarm as string}`, ev.attributes.alarm as string)})`
              : t(`notifications.events.${ev.type}`, ev.type),
            [t('commands.result', { defaultValue: 'Result' })]: getCommandResultContent(ev),
            [t('reports.driver')]: driverName,
            [t('reports.time')]: format(new Date(ev.eventTime), 'yyyy-MM-dd HH:mm'),
          };
        });
        ws = XLSX.utils.json_to_sheet(data);
        break;
      }
      case 'route': {
        const data = positions.map(pos => {
          const row: Record<string, string | number> = {
            [t('reports.time')]: format(new Date(pos.fixTime), 'yyyy-MM-dd HH:mm:ss'),
            [t('reports.latitude')]: pos.latitude.toFixed(6),
            [t('reports.longitude')]: pos.longitude.toFixed(6),
            [t('devices.speed')]: `${Math.round(pos.speed * 1.852)} km/h`,
          };
          for (const col of activeRouteExtraColumns) {
            row[t(col.labelKey)] = col.csv(pos, t);
          }
          row[t('devices.address')] = pos.address || '';
          return row;
        });
        ws = XLSX.utils.json_to_sheet(data);
        // Turn link columns (e.g. Google Maps) into real clickable hyperlinks.
        activeRouteExtraColumns.forEach((col, extraIndex) => {
          if (!col.href) return;
          const colIndex = 4 + extraIndex; // time, latitude, longitude, speed, then extras
          positions.forEach((pos, rowIndex) => {
            const ref = XLSX.utils.encode_cell({ c: colIndex, r: rowIndex + 1 });
            const target = col.href!(pos);
            ws[ref] = { t: 's', v: String(col.csv(pos, t)), l: { Target: target, Tooltip: target } };
          });
        });
        break;
      }
      case 'raw': {
        // Collect all unique attribute keys
        const attrKeys = new Set<string>();
        positions.forEach(pos => {
          if (pos.attributes) Object.keys(pos.attributes).forEach(k => attrKeys.add(k));
        });
        const sortedAttrKeys = Array.from(attrKeys).sort();

        const data = positions.map(pos => {
          const row: Record<string, unknown> = {
            'ID': pos.id,
            [t('reports.time')]: format(new Date(pos.fixTime), 'yyyy-MM-dd HH:mm:ss'),
            'Device Time': format(new Date(pos.deviceTime), 'yyyy-MM-dd HH:mm:ss'),
            'Server Time': format(new Date(pos.serverTime), 'yyyy-MM-dd HH:mm:ss'),
            'Delay (s)': Math.round((new Date(pos.serverTime).getTime() - new Date(pos.fixTime).getTime()) / 1000),
            'Protocol': pos.protocol,
            'Valid': pos.valid ? 'Yes' : 'No',
            [t('reports.latitude')]: pos.latitude,
            [t('reports.longitude')]: pos.longitude,
            'Altitude': pos.altitude,
            [t('devices.speed') + ' (km/h)']: Math.round(pos.speed * 1.852),
            'Course': pos.course,
            [t('devices.address')]: pos.address || '',
          };
          sortedAttrKeys.forEach(key => {
            row[key] = pos.attributes?.[key] ?? '';
          });
          return row;
        });
        ws = XLSX.utils.json_to_sheet(data);
        break;
      }
      case 'temperature': {
        const sensors = sortSensorKeys(new Set(positions.flatMap(pos => Object.keys(getTemperatureReadings(pos)))));
        const data = [...positions]
          .sort((a, b) => a.deviceId - b.deviceId || new Date(a.fixTime).getTime() - new Date(b.fixTime).getTime())
          .map(pos => {
            const readings = getTemperatureReadings(pos);
            const row: Record<string, string | number> = {
              [t('devices.name')]: getDeviceName(pos.deviceId),
              [t('reports.time')]: format(new Date(pos.fixTime), 'yyyy-MM-dd HH:mm:ss'),
            };
            sensors.forEach(sensor => {
              row[`${temperatureSensorLabel(t, sensor)} (°C)`] = readings[sensor] ?? '';
            });
            return row;
          });
        ws = XLSX.utils.json_to_sheet(data);
        break;
      }
      case 'battery-voltage': {
        const data = positions.map(pos => ({
          [t('devices.name')]: getDeviceName(pos.deviceId),
          [t('reports.time')]: format(new Date(pos.fixTime), 'yyyy-MM-dd HH:mm:ss'),
          [t('reports.batteryVoltage')]: getVehicleBatteryVoltage(pos),
        }));
        ws = XLSX.utils.json_to_sheet(data);
        break;
      }
      case 'driver': {
        const data = driverTrips.map(dr => ({
          [t('reports.driverName')]: dr.driverName,
          [t('reports.tripCount')]: dr.trips,
          [t('history.distance')]: `${(dr.distance / 1000).toFixed(1)} km`,
          [t('history.duration')]: formatHours(dr.duration),
          [t('history.avgSpeed')]: `${Math.round(dr.averageSpeed * 1.852)} km/h`,
          [t('history.maxSpeed')]: `${Math.round(dr.maxSpeed * 1.852)} km/h`,
        }));
        ws = XLSX.utils.json_to_sheet(data);
        break;
      }
      case 'speeding': {
        const data = speedingEvents.map(ev => ({
          [t('devices.name')]: ev.deviceName,
          [t('reports.driverName')]: ev.driverName || '',
          [t('history.start')]: format(new Date(ev.startTime), 'yyyy-MM-dd HH:mm:ss'),
          [t('history.end')]: format(new Date(ev.endTime), 'yyyy-MM-dd HH:mm:ss'),
          [t('history.duration')]: formatHours(ev.duration),
          [t('history.maxSpeed')]: `${Math.round(ev.maxSpeed * 1.852)} km/h`,
          [t('history.avgSpeed')]: `${Math.round(ev.avgSpeed * 1.852)} km/h`,
          [t('reports.speedLimit')]: `${speedLimit} km/h`,
          [t('devices.address')]: ev.address,
        }));
        ws = XLSX.utils.json_to_sheet(data);
        break;
      }
      case 'ranking': {
        const data = rankingData.map(dr => ({
          '#': dr.rank,
          [t('reports.driverName')]: dr.driverName,
          [t('reports.tripCount')]: dr.trips,
          [t('history.distance')]: `${(dr.distance / 1000).toFixed(1)} km`,
          [t('history.duration')]: formatHours(dr.duration),
          [t('history.avgSpeed')]: `${Math.round(dr.averageSpeed * 1.852)} km/h`,
          [t('history.maxSpeed')]: `${Math.round(dr.maxSpeed * 1.852)} km/h`,
          [t('reports.idleEvents')]: dr.idleEvents,
          [t('notifications.alarms.overspeed')]: dr.overspeed,
          [t('notifications.alarms.hardBraking')]: dr.harshBraking,
          [t('notifications.alarms.hardAcceleration')]: dr.harshAcceleration,
          [t('notifications.alarms.hardCornering')]: dr.harshCornering,
          [t('notifications.alarms.accident')]: dr.severe,
          [t('reports.score')]: dr.score,
        }));
        ws = XLSX.utils.json_to_sheet(data);
        break;
      }
    }

    XLSX.utils.book_append_sheet(wb, ws!, t(getReportTypeLabelKey(reportType)));
    XLSX.writeFile(wb, `report_${reportType}_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
  }, [reportType, trips, summaries, stops, events, positions, driverTrips, speedingEvents, rankingData, hoursReport, speedLimit, devices, drivers, formatHours, t, getDeviceName, activeRouteExtraColumns]);

  // Chart data computations (must be before any early returns)
  const activityChartData = useMemo(() => {
    if (!dateRange?.from || !dateRange?.to) return [];
    const days = eachDayOfInterval({ start: dateRange.from, end: dateRange.to });
    return days.map(day => {
      const dayStr = format(day, 'MMM d');
      const dayTrips = trips.filter(t => format(new Date(t.startTime), 'yyyy-MM-dd') === format(day, 'yyyy-MM-dd'));
      return {
        date: dayStr,
        distance: parseFloat((dayTrips.reduce((s, t) => s + t.distance, 0) / 1000).toFixed(1)),
        trips: dayTrips.length,
      };
    });
  }, [trips, dateRange]);

  const eventsChartData = useMemo(() => {
    const counts = new Map<string, number>();
    for (const ev of events) {
      counts.set(ev.type, (counts.get(ev.type) || 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([type, count]) => ({ type, count }))
      .sort((a, b) => b.count - a.count);
  }, [events]);

  const driverChartData = useMemo(() =>
    [...driverTrips]
      .sort((a, b) => b.distance - a.distance)
      .slice(0, 15)
      .map(d => ({
        name: d.driverName.length > 16 ? d.driverName.slice(0, 15) + '…' : d.driverName,
        fullName: d.driverName,
        distance: parseFloat((d.distance / 1000).toFixed(1)),
        trips: d.trips,
        avgSpeed: Math.round(d.averageSpeed * 1.852),
      }))
  , [driverTrips]);

  if (isLoading && !hasLoaded) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
          <p className="text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  const needsDevice = reportType !== 'driver' && reportType !== 'ranking' && reportType !== 'driver-chart';
  const canGenerate = dateRange?.from && dateRange?.to && (!needsDevice || selectedDeviceId !== '');

  const headerContent = (
    <div className="flex items-center gap-2 flex-wrap w-full">
        <Select value={reportType} onValueChange={(v) => { setReportType(v as ReportType); setHasGenerated(false); clearResults(); }}>
          <SelectTrigger className="h-10 w-[120px] xl:w-[160px]">
            <SelectValue placeholder={t('reports.selectReportType')} />
          </SelectTrigger>
          <SelectContent>
            {[...REPORT_TYPE_OPTIONS]
              .sort((a, b) => t(a.labelKey).localeCompare(t(b.labelKey)))
              .map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {t(option.labelKey)}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>

        {reportType === 'speeding' && (
          <Select value={speedLimit.toString()} onValueChange={(v) => setSpeedLimit(Number(v))}>
            <SelectTrigger className="h-10 w-[100px] xl:w-[130px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[50, 60, 70, 80, 90, 100, 110, 120, 130].map(v => (
                <SelectItem key={v} value={v.toString()}>{v} km/h</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {reportType === 'temperature' && (
          <div className="flex items-center gap-1.5" title={t('reports.temperature.allowedRange')}>
            <Input
              type="number"
              inputMode="decimal"
              step="0.5"
              value={tempRangeMin}
              onChange={(event) => setTempRangeMin(event.target.value)}
              placeholder={t('reports.temperature.rangeMin')}
              className="h-10 w-[84px]"
            />
            <span className="text-muted-foreground text-sm">–</span>
            <Input
              type="number"
              inputMode="decimal"
              step="0.5"
              value={tempRangeMax}
              onChange={(event) => setTempRangeMax(event.target.value)}
              placeholder={t('reports.temperature.rangeMax')}
              className="h-10 w-[84px]"
            />
            <span className="text-muted-foreground text-sm">°C</span>
          </div>
        )}

        {reportType === 'idle' && (
          <Select value={minIdleTime.toString()} onValueChange={(v) => setMinIdleTime(Number(v))}>
            <SelectTrigger className="h-10 w-[110px] xl:w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[5, 10, 15, 20, 30, 45, 60].map(v => (
                <SelectItem key={v} value={v.toString()}>{t('reports.minIdleTime', { min: v })}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {reportType === 'route' && (
          <Popover open={routeColumnsOpen} onOpenChange={setRouteColumnsOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" role="combobox" aria-expanded={routeColumnsOpen} className="h-10 w-[150px] xl:w-[190px] justify-between font-normal">
                <span className="truncate">
                  {t('reports.extraColumns')}
                  {activeRouteExtraColumns.length > 0 && ` (${activeRouteExtraColumns.length})`}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[240px] p-0" align="start">
              <Command>
                <CommandList>
                  <CommandGroup>
                    {ROUTE_EXTRA_COLUMNS.map(col => (
                      <CommandItem
                        key={col.id}
                        onSelect={() => setRouteExtraColumns(prev => {
                          const next = new Set(prev);
                          if (next.has(col.id)) next.delete(col.id);
                          else next.add(col.id);
                          return next;
                        })}
                      >
                        <Check className={cn('mr-2 h-4 w-4', routeExtraColumns.has(col.id) ? 'opacity-100' : 'opacity-0')} />
                        {t(col.labelKey)}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )}

        {reportType === 'driver' && (
          <Select value={selectedDriverId} onValueChange={setSelectedDriverId}>
            <SelectTrigger className="h-10 w-[140px] xl:w-[200px]">
              <SelectValue placeholder={t('reports.selectDriver')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('common.all')}</SelectItem>
              {drivers.map((driver) => (
                <SelectItem key={driver.id} value={driver.uniqueId}>
                  {driver.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {reportType !== 'driver' && reportType !== 'ranking' && reportType !== 'driver-chart' && (
          <Popover open={devicePickerOpen} onOpenChange={setDevicePickerOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" role="combobox" aria-expanded={devicePickerOpen} className="h-10 w-[140px] xl:w-[200px] justify-between font-normal">
                <span className="truncate">
                  {selectedDeviceId === 'all'
                    ? t('common.all')
                    : (devices.find(d => d.id.toString() === selectedDeviceId)?.name ?? t('history.selectDevice'))}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[220px] p-0" align="start">
              <Command>
                <CommandInput placeholder={t('devices.searchPlaceholder')} className="h-9" />
                <CommandList>
                  <CommandEmpty>{t('devices.noDevicesMatch')}</CommandEmpty>
                  <CommandGroup>
                    <CommandItem value="all" onSelect={() => { setSelectedDeviceId('all'); setDevicePickerOpen(false); }}>
                      <Check className={cn('mr-2 h-4 w-4', selectedDeviceId === 'all' ? 'opacity-100' : 'opacity-0')} />
                      {t('common.all')}
                    </CommandItem>
                    {visibleDevices.map((device) => (
                      <CommandItem
                        key={device.id}
                        value={device.name}
                        onSelect={() => { setSelectedDeviceId(device.id.toString()); setDevicePickerOpen(false); }}
                      >
                        <Check className={cn('mr-2 h-4 w-4', selectedDeviceId === device.id.toString() ? 'opacity-100' : 'opacity-0')} />
                        {device.name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )}
      {isFlutter && <div className="w-full h-0" />}

      <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="h-10 gap-2 shrink-0">
              <Calendar className="h-4 w-4" />
              {dateRange?.from ? (
                dateRange.to ? (
                  <>
                    <span className="xl:hidden">{format(dateRange.from, "d/M HH:mm")} - {format(dateRange.to, "d/M HH:mm")}</span>
                    <span className="hidden xl:inline">{format(dateRange.from, "MMM d, HH:mm", { locale: dateFnsLocale })} - {format(dateRange.to, "MMM d, HH:mm", { locale: dateFnsLocale })}</span>
                  </>
                ) : (
                  <span>{format(dateRange.from, "d/M/yy HH:mm")}</span>
                )
              ) : (
                t('history.selectDates')
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto max-w-[calc(100vw-1rem)] p-0" align="start">
            <CalendarComponent
              mode="range"
              selected={dateRange}
              onSelect={(range) => {
                const applyTime = (date: Date, time: string) => {
                  const [h, m] = time.split(':').map(Number);
                  const d = new Date(date);
                  d.setHours(h, m, 0, 0);
                  return d;
                };
                setDateRange(range ? {
                  from: range.from ? applyTime(range.from, fromTime) : undefined,
                  to: range.to ? applyTime(range.to, toTime) : (range.from ? applyTime(range.from, toTime) : undefined),
                } : undefined);
              }}
              numberOfMonths={isMobile ? 1 : 2}
              disabled={(date) => date > new Date()}
            />
            <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-t border-border">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">{t('history.start')}</span>
                <input
                  type="time"
                  value={fromTime}
                  onChange={(e) => {
                    const time = e.target.value;
                    setFromTime(time);
                    if (dateRange?.from) {
                      const [h, m] = time.split(':').map(Number);
                      const d = new Date(dateRange.from);
                      d.setHours(h, m, 0, 0);
                      setDateRange(prev => prev ? { ...prev, from: d } : prev);
                    }
                  }}
                  className="text-sm border border-border rounded px-2 py-1 bg-background"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">{t('history.end')}</span>
                <input
                  type="time"
                  value={toTime}
                  onChange={(e) => {
                    const time = e.target.value;
                    setToTime(time);
                    if (dateRange?.to) {
                      const [h, m] = time.split(':').map(Number);
                      const d = new Date(dateRange.to);
                      d.setHours(h, m, 0, 0);
                      setDateRange(prev => prev ? { ...prev, to: d } : prev);
                    }
                  }}
                  className="text-sm border border-border rounded px-2 py-1 bg-background"
                />
              </div>
            </div>
          </PopoverContent>
        </Popover>

        <Button onClick={generateReport} disabled={!canGenerate || isGenerating} className="h-10 gap-2">
          {isGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
          <span className="hidden xl:inline">{t('reports.generate')}</span>
        </Button>

        {hasGenerated && hasData() && (
          <>
            <Button variant="outline" onClick={exportToExcel} className="h-10 gap-2 shrink-0">
              <Download className="h-4 w-4" />
              <span className="hidden xl:inline">{t('reports.export')}</span>
            </Button>
            {!isFlutter && (
              <Button variant="outline" onClick={() => window.print()} className="h-10 gap-2 shrink-0">
                <Printer className="h-4 w-4" />
                <span className="hidden xl:inline">{t('reports.print')}</span>
              </Button>
            )}
          </>
        )}
    </div>
  );

  const renderTable = () => {
    if (!hasGenerated) return null;

    if (!hasData()) {
      return (
        <div className="text-center py-12 text-muted-foreground">
          <FileSpreadsheet className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>{t('reports.noData')}</p>
        </div>
      );
    }

    switch (reportType) {
      case 'trips':
        return (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('devices.name')}</TableHead>
                <TableHead>{t('reports.driver')}</TableHead>
                <TableHead>{t('history.start')}</TableHead>
                <TableHead>{t('history.end')}</TableHead>
                <TableHead>{t('history.startAddress')}</TableHead>
                <TableHead>{t('history.endAddress')}</TableHead>
                <TableHead className="text-right">{t('history.distance')}</TableHead>
                <TableHead className="text-right">{t('history.duration')}</TableHead>
                <TableHead className="text-right">{t('history.avgSpeed')}</TableHead>
                <TableHead className="text-right">{t('history.maxSpeed')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {trips.map((trip, i) => (
                <TableRow key={i}>
                  <TableCell className="font-medium">{trip.deviceName}</TableCell>
                  <TableCell>{trip.driverName || '—'}</TableCell>
                  <TableCell title={format(new Date(trip.startTime), 'yyyy-MM-dd HH:mm:ss')}>{format(new Date(trip.startTime), 'MMM d, HH:mm', { locale: dateFnsLocale })}</TableCell>
                  <TableCell title={format(new Date(trip.endTime), 'yyyy-MM-dd HH:mm:ss')}>{format(new Date(trip.endTime), 'MMM d, HH:mm', { locale: dateFnsLocale })}</TableCell>
                  <TableCell className="max-w-[200px]"><TwoLineAddress address={trip.startAddress} /></TableCell>
                  <TableCell className="max-w-[200px]"><TwoLineAddress address={trip.endAddress} /></TableCell>
                  <TableCell className="text-right">
                    <Link
                      to={`/history/trips?deviceId=${trip.deviceId}&date=${format(new Date(trip.startTime), 'yyyy-MM-dd')}`}
                      className="text-blue-600 hover:underline"
                    >
                      {(trip.distance / 1000).toFixed(1)} km
                    </Link>
                  </TableCell>
                  <TableCell className="text-right">{formatHours(trip.duration)}</TableCell>
                  <TableCell className="text-right">{Math.round(trip.averageSpeed * 1.852)} km/h</TableCell>
                  <TableCell className="text-right">{Math.round(trip.maxSpeed * 1.852)} km/h</TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow className="font-semibold">
                <TableCell colSpan={6}>{t('reports.total')} ({trips.length})</TableCell>
                <TableCell className="text-right">{(trips.reduce((sum, t) => sum + t.distance, 0) / 1000).toFixed(1)} km</TableCell>
                <TableCell className="text-right">{formatHours(trips.reduce((sum, t) => sum + t.duration, 0))}</TableCell>
                <TableCell className="text-right">
                  {trips.length > 0 ? Math.round(trips.reduce((sum, t) => sum + t.averageSpeed, 0) / trips.length * 1.852) : 0} km/h
                </TableCell>
                <TableCell className="text-right">{Math.round(Math.max(...trips.map(t => t.maxSpeed)) * 1.852)} km/h</TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        );

      case 'summary':
        return (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('devices.name')}</TableHead>
                <TableHead className="text-right">{t('history.distance')}</TableHead>
                <TableHead className="text-right">{t('history.avgSpeed')}</TableHead>
                <TableHead className="text-right">{t('history.maxSpeed')}</TableHead>
                <TableHead className="text-right">{t('history.engineHours')}</TableHead>
                <TableHead className="text-right">{t('history.fuel')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {summaries.map((s) => (
                <TableRow key={s.deviceId}>
                  <TableCell className="font-medium">{s.deviceName}</TableCell>
                  <TableCell className="text-right">{(s.distance / 1000).toFixed(1)} km</TableCell>
                  <TableCell className="text-right">{Math.round(s.averageSpeed * 1.852)} km/h</TableCell>
                  <TableCell className="text-right">{Math.round(s.maxSpeed * 1.852)} km/h</TableCell>
                  <TableCell className="text-right">{formatHours(s.engineHours)}</TableCell>
                  <TableCell className="text-right">{s.spentFuel.toFixed(1)} L</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        );

      case 'hours':
        return (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('devices.name')}</TableHead>
                <TableHead>{t('history.start')}</TableHead>
                <TableHead>{t('history.end')}</TableHead>
                <TableHead className="text-right">{t('history.engineHours')}</TableHead>
                <TableHead className="text-right">{t('reports.movingHours')}</TableHead>
                <TableHead className="text-right">{t('reports.idleTime')}</TableHead>
                <TableHead className="text-right">{t('history.distance')}</TableHead>
                <TableHead className="text-right">{t('history.avgSpeed')}</TableHead>
                <TableHead className="text-right">{t('history.maxSpeed')}</TableHead>
                <TableHead className="text-right">{t('reports.idlePercent')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {hoursReport.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="font-medium">{row.deviceName}</TableCell>
                  <TableCell title={format(new Date(row.startTime), 'yyyy-MM-dd HH:mm:ss')}>
                    {format(new Date(row.startTime), 'MMM d, HH:mm', { locale: dateFnsLocale })}
                  </TableCell>
                  <TableCell title={format(new Date(row.endTime), 'yyyy-MM-dd HH:mm:ss')}>
                    {format(new Date(row.endTime), 'MMM d, HH:mm', { locale: dateFnsLocale })}
                  </TableCell>
                  <TableCell className="text-right font-medium">{formatHours(row.engineHours)}</TableCell>
                  <TableCell className="text-right">{formatHours(row.movingHours)}</TableCell>
                  <TableCell className="text-right text-amber-600">{formatHours(row.idleHours)}</TableCell>
                  <TableCell className="text-right">{(row.distance / 1000).toFixed(1)} km</TableCell>
                  <TableCell className="text-right">{Math.round(row.averageSpeed * 1.852)} km/h</TableCell>
                  <TableCell className="text-right">{Math.round(row.maxSpeed * 1.852)} km/h</TableCell>
                  <TableCell className="text-right">{Math.round(row.idleRatio * 100)}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow className="font-semibold">
                <TableCell colSpan={3}>{t('reports.total')} ({hoursReport.length})</TableCell>
                <TableCell className="text-right">{formatHours(hoursReport.reduce((sum, row) => sum + row.engineHours, 0))}</TableCell>
                <TableCell className="text-right">{formatHours(hoursReport.reduce((sum, row) => sum + row.movingHours, 0))}</TableCell>
                <TableCell className="text-right">{formatHours(hoursReport.reduce((sum, row) => sum + row.idleHours, 0))}</TableCell>
                <TableCell className="text-right">{(hoursReport.reduce((sum, row) => sum + row.distance, 0) / 1000).toFixed(1)} km</TableCell>
                <TableCell className="text-right">
                  {Math.round((
                    hoursReport.reduce((sum, row) => sum + row.averageSpeed * row.engineHours, 0) /
                    Math.max(1, hoursReport.reduce((sum, row) => sum + row.engineHours, 0))
                  ) * 1.852)} km/h
                </TableCell>
                <TableCell className="text-right">{Math.round(Math.max(...hoursReport.map(row => row.maxSpeed)) * 1.852)} km/h</TableCell>
                <TableCell className="text-right">
                  {Math.round(
                    hoursReport.reduce((sum, row) => sum + row.idleHours, 0) /
                    Math.max(1, hoursReport.reduce((sum, row) => sum + row.engineHours, 0)) * 100
                  )}%
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        );

      case 'stops':
        return (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('devices.name')}</TableHead>
                <TableHead>{t('history.start')}</TableHead>
                <TableHead>{t('history.end')}</TableHead>
                <TableHead className="text-right">{t('history.duration')}</TableHead>
                <TableHead>{t('devices.address')}</TableHead>
                <TableHead className="text-right">{t('history.engineHours')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stops.map((stop, i) => (
                <TableRow key={i}>
                  <TableCell className="font-medium">{stop.deviceName}</TableCell>
                  <TableCell>{format(new Date(stop.startTime), 'MMM d, HH:mm', { locale: dateFnsLocale })}</TableCell>
                  <TableCell>{format(new Date(stop.endTime), 'MMM d, HH:mm', { locale: dateFnsLocale })}</TableCell>
                  <TableCell className="text-right">{formatHours(stop.duration)}</TableCell>
                  <TableCell className="max-w-[200px]"><TwoLineAddress address={stop.address} /></TableCell>
                  <TableCell className="text-right">{formatHours(stop.engineHours)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        );

      case 'idle':
        return (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('devices.name')}</TableHead>
                <TableHead>{t('reports.driver')}</TableHead>
                <TableHead>{t('history.start')}</TableHead>
                <TableHead>{t('history.end')}</TableHead>
                <TableHead className="text-right">{t('reports.idleTime')}</TableHead>
                <TableHead className="text-right">{t('history.duration')}</TableHead>
                <TableHead>{t('devices.address')}</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stops.map((stop, i) => (
                <TableRow key={i}>
                  <TableCell className="font-medium">{stop.deviceName}</TableCell>
                  <TableCell>{stop.driverName || '—'}</TableCell>
                  <TableCell>{format(new Date(stop.startTime), 'MMM d, HH:mm', { locale: dateFnsLocale })}</TableCell>
                  <TableCell>{format(new Date(stop.endTime), 'MMM d, HH:mm', { locale: dateFnsLocale })}</TableCell>
                  <TableCell className="text-right font-medium text-amber-600">{formatHours(stop.engineHours)}</TableCell>
                  <TableCell className="text-right">{formatHours(stop.duration)}</TableCell>
                  <TableCell className="max-w-[200px]"><TwoLineAddress address={stop.address} /></TableCell>
                  <TableCell>
                    <a
                      href={`/history/trips?deviceId=${stop.deviceId}&date=${format(new Date(stop.startTime), 'yyyy-MM-dd')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline whitespace-nowrap text-sm"
                    >
                      Route
                    </a>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        );

      case 'events': {
        const eventTypes = Array.from(new Set(events.map(ev => ev.type))).sort();
        const filteredEvents = eventTypeFilter.size === 0 ? events : events.filter(ev => eventTypeFilter.has(ev.type));
        const toggleEventType = (type: string) => {
          setEventTypeFilter(prev => {
            const next = new Set(prev);
            if (next.has(type)) next.delete(type); else next.add(type);
            return next;
          });
        };
        return (
          <div className="space-y-3">
            {eventTypes.length > 1 && (
              <div className="flex flex-wrap gap-2 px-1">
                {eventTypes.map(type => (
                  <Badge
                    key={type}
                    variant={eventTypeFilter.has(type) ? 'default' : 'outline'}
                    className="cursor-pointer select-none"
                    onClick={() => toggleEventType(type)}
                  >
                    {t(`notifications.events.${type}`, type)}
                  </Badge>
                ))}
              </div>
            )}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('devices.name')}</TableHead>
                  <TableHead>{t('reports.type')}</TableHead>
                  <TableHead>{t('commands.result', { defaultValue: 'Result' })}</TableHead>
                  <TableHead>{t('reports.driver')}</TableHead>
                  <TableHead>{t('reports.time')}</TableHead>
                  <TableHead>{t('devices.address')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEvents.map((ev) => {
                  const driverUniqueId = ev.attributes?.driverUniqueId as string | undefined;
                  const driverName = driverUniqueId
                    ? (drivers.find(d => d.uniqueId === driverUniqueId)?.name || driverUniqueId)
                    : undefined;
                  const commandResultContent = getCommandResultContent(ev);
                  const pos = eventPositions.get(ev.positionId);
                  const activityLink = `/history/trips?deviceId=${ev.deviceId}&date=${format(new Date(ev.eventTime), 'yyyy-MM-dd')}${pos?.fixTime ? `&time=${new Date(pos.fixTime).getTime()}` : ''}`;
                  return (
                    <TableRow key={ev.id}>
                      <TableCell className="font-medium">{getDeviceName(ev.deviceId)}</TableCell>
                      <TableCell>
                        {t(`notifications.events.${ev.type}`, ev.type)}
                        {ev.type === 'alarm' && ev.attributes?.alarm && (
                          <span className="ml-1 text-muted-foreground">
                            ({t(`notifications.alarms.${ev.attributes.alarm as string}`, ev.attributes.alarm as string)})
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="max-w-[320px] font-mono text-xs break-all">
                        {commandResultContent || '—'}
                      </TableCell>
                      <TableCell>{driverName || '—'}</TableCell>
                      <TableCell>{format(new Date(ev.eventTime), 'MMM d, HH:mm', { locale: dateFnsLocale })}</TableCell>
                      <TableCell className="max-w-[220px]">
                        {pos?.address ? (
                          <a href={activityLink} className="text-primary hover:underline truncate block" title={pos.address}>
                            {pos.address}
                          </a>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        );
      }

      case 'route': {
        const totalPages = Math.ceil(positions.length / POSITIONS_PAGE_SIZE);
        const pagePositions = positions.slice(routePage * POSITIONS_PAGE_SIZE, (routePage + 1) * POSITIONS_PAGE_SIZE);
        const routePagination = (
          <div className="flex items-center justify-between text-sm text-muted-foreground px-1">
            <span>{positions.length.toLocaleString()} rows{totalPages > 1 ? ` · page ${routePage + 1} of ${totalPages}` : ''}</span>
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setRoutePage(p => Math.max(0, p - 1))} disabled={routePage === 0}>←</Button>
                <Button variant="outline" size="sm" onClick={() => setRoutePage(p => Math.min(totalPages - 1, p + 1))} disabled={routePage >= totalPages - 1}>→</Button>
              </div>
            )}
          </div>
        );
        return (
          <div className="space-y-2">
          {routePagination}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('reports.time')}</TableHead>
                <TableHead className="text-right">{t('reports.latitude')}</TableHead>
                <TableHead className="text-right">{t('reports.longitude')}</TableHead>
                <TableHead className="text-right">{t('devices.speed')}</TableHead>
                <TableHead className="text-right">{t('devices.odometer')}</TableHead>
                <TableHead className="text-right">{t('devices.engineHours')}</TableHead>
                {activeRouteExtraColumns.map(col => (
                  <TableHead key={col.id} className={col.align === 'right' ? 'text-right' : undefined}>
                    {t(col.labelKey)}
                  </TableHead>
                ))}
                <TableHead>{t('devices.address')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pagePositions.map((pos) => (
                <TableRow key={pos.id}>
                  <TableCell>{format(new Date(pos.fixTime), 'MMM d, HH:mm:ss', { locale: dateFnsLocale })}</TableCell>
                  <TableCell className="text-right">{pos.latitude.toFixed(6)}</TableCell>
                  <TableCell className="text-right">{pos.longitude.toFixed(6)}</TableCell>
                  <TableCell className="text-right">{Math.round(pos.speed * 1.852)} km/h</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {pos.attributes?.totalDistance != null
                      ? `${((pos.attributes.totalDistance as number) / 1000).toLocaleString(undefined, { maximumFractionDigits: 0 })} km`
                      : '—'}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {pos.attributes?.hours != null
                      ? `${((pos.attributes.hours as number) / 3600000).toLocaleString(undefined, { maximumFractionDigits: 1 })} h`
                      : '—'}
                  </TableCell>
                  {activeRouteExtraColumns.map(col => (
                    <TableCell key={col.id} className={col.align === 'right' ? 'text-right tabular-nums' : undefined}>
                      {col.cell(pos, t)}
                    </TableCell>
                  ))}
                  <TableCell className="max-w-[200px]"><TwoLineAddress address={pos.address} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {positions.length > POSITIONS_PAGE_SIZE && routePagination}
          </div>
        );
      }

      case 'driver':
        return (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('reports.driverName')}</TableHead>
                <TableHead className="text-right">{t('reports.tripCount')}</TableHead>
                <TableHead className="text-right">{t('history.distance')}</TableHead>
                <TableHead className="text-right">{t('history.duration')}</TableHead>
                <TableHead className="text-right">{t('history.avgSpeed')}</TableHead>
                <TableHead className="text-right">{t('history.maxSpeed')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {driverTrips.map((dr) => (
                <TableRow key={dr.driverUniqueId || 'none'}>
                  <TableCell className="font-medium">{dr.driverName}</TableCell>
                  <TableCell className="text-right">{dr.trips}</TableCell>
                  <TableCell className="text-right">{(dr.distance / 1000).toFixed(1)} km</TableCell>
                  <TableCell className="text-right">{formatHours(dr.duration)}</TableCell>
                  <TableCell className="text-right">{Math.round(dr.averageSpeed * 1.852)} km/h</TableCell>
                  <TableCell className="text-right">{Math.round(dr.maxSpeed * 1.852)} km/h</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        );

      case 'speeding':
        return (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('devices.name')}</TableHead>
                <TableHead>{t('reports.driverName')}</TableHead>
                <TableHead>{t('history.start')}</TableHead>
                <TableHead>{t('history.end')}</TableHead>
                <TableHead className="text-right">{t('history.duration')}</TableHead>
                <TableHead className="text-right">{t('history.maxSpeed')}</TableHead>
                <TableHead className="text-right">{t('history.avgSpeed')}</TableHead>
                <TableHead>{t('devices.address')}</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {speedingEvents.map((ev, i) => (
                <TableRow key={i}>
                  <TableCell className="font-medium">{ev.deviceName}</TableCell>
                  <TableCell>{ev.driverName || '—'}</TableCell>
                  <TableCell className="whitespace-nowrap">{format(new Date(ev.startTime), 'MMM d, HH:mm:ss', { locale: dateFnsLocale })}</TableCell>
                  <TableCell className="whitespace-nowrap">{format(new Date(ev.endTime), 'MMM d, HH:mm:ss', { locale: dateFnsLocale })}</TableCell>
                  <TableCell className="text-right">{formatHours(ev.duration)}</TableCell>
                  <TableCell className="text-right font-medium text-destructive">{Math.round(ev.maxSpeed * 1.852)} km/h</TableCell>
                  <TableCell className="text-right">{Math.round(ev.avgSpeed * 1.852)} km/h</TableCell>
                  <TableCell className="max-w-[240px]"><TwoLineAddress address={ev.address} /></TableCell>
                  <TableCell>
                    <a
                      href={`https://www.google.com/maps?q=${ev.latitude},${ev.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline whitespace-nowrap text-sm"
                    >
                      📍 Map
                    </a>
                    <div className="text-xs text-muted-foreground mt-0.5 whitespace-nowrap">
                      {format(new Date(ev.fixTime), 'HH:mm:ss')} · {Math.round(ev.startSpeed * 1.852)} km/h
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        );

      case 'ranking': {
        const rankColor = (rank: number) =>
          rank === 1 ? 'text-yellow-500' : rank === 2 ? 'text-slate-400' : rank === 3 ? 'text-amber-600' : 'text-muted-foreground';
        const scoreColor = (score: number) =>
          score >= 80 ? 'text-green-600' : score >= 60 ? 'text-yellow-600' : 'text-destructive';

        const infractionCell = (value: number) =>
          `text-right ${value > 0 ? 'text-destructive font-medium' : 'text-muted-foreground'}`;

        return (
          <div className="space-y-4">
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>{t('reports.driverName')}</TableHead>
                  <TableHead className="text-right">{t('reports.tripCount')}</TableHead>
                  <TableHead className="text-right">{t('history.distance')}</TableHead>
                  <TableHead className="text-right">{t('history.duration')}</TableHead>
                  <TableHead className="text-right">{t('history.avgSpeed')}</TableHead>
                  <TableHead className="text-right">{t('history.maxSpeed')}</TableHead>
                  <TableHead className="text-right" title={t('reports.idleEvents')}>⏱</TableHead>
                  <TableHead className="text-right" title={t('notifications.alarms.overspeed')}>💨</TableHead>
                  <TableHead className="text-right" title={t('notifications.alarms.hardBraking')}>🛑</TableHead>
                  <TableHead className="text-right" title={t('notifications.alarms.hardAcceleration')}>⚡</TableHead>
                  <TableHead className="text-right" title={t('notifications.alarms.hardCornering')}>↪</TableHead>
                  <TableHead className="text-right" title={t('notifications.alarms.accident')}>💥</TableHead>
                  <TableHead className="text-right">{t('reports.score')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rankingData.map((dr) => (
                  <TableRow key={dr.driverUniqueId || 'none'}>
                    <TableCell className={`font-bold text-base ${rankColor(dr.rank)}`}>{dr.rank}</TableCell>
                    <TableCell className="font-medium">{dr.driverName}</TableCell>
                    <TableCell className="text-right">{dr.trips}</TableCell>
                    <TableCell className="text-right">{(dr.distance / 1000).toFixed(1)} km</TableCell>
                    <TableCell className="text-right">{formatHours(dr.duration)}</TableCell>
                    <TableCell className="text-right">{Math.round(dr.averageSpeed * 1.852)} km/h</TableCell>
                    <TableCell className="text-right">{Math.round(dr.maxSpeed * 1.852)} km/h</TableCell>
                    <TableCell className={infractionCell(dr.idleEvents)}>{dr.idleEvents}</TableCell>
                    <TableCell className={infractionCell(dr.overspeed)}>{dr.overspeed}</TableCell>
                    <TableCell className={infractionCell(dr.harshBraking)}>{dr.harshBraking}</TableCell>
                    <TableCell className={infractionCell(dr.harshAcceleration)}>{dr.harshAcceleration}</TableCell>
                    <TableCell className={infractionCell(dr.harshCornering)}>{dr.harshCornering}</TableCell>
                    <TableCell className={infractionCell(dr.severe)}>{dr.severe}</TableCell>
                    <TableCell className={`text-right font-bold ${scoreColor(dr.score)}`}>{dr.score}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
            <p className="text-xs text-muted-foreground px-1">{t('reports.rankingScoreNote')}</p>
          </div>
        );
      }

      case 'raw': {
        // Collect all unique attribute keys across all positions
        const attrKeys = new Set<string>();
        positions.forEach(pos => {
          if (pos.attributes) Object.keys(pos.attributes).forEach(k => attrKeys.add(k));
        });
        const sortedAttrKeys = Array.from(attrKeys).sort();
        const totalPages = Math.ceil(positions.length / POSITIONS_PAGE_SIZE);
        const pagePositions = positions.slice(rawPage * POSITIONS_PAGE_SIZE, (rawPage + 1) * POSITIONS_PAGE_SIZE);
        const thClass = "sticky top-0 bg-muted h-10 px-4 text-left align-middle text-xs font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap border-b border-border";
        const thRClass = "sticky top-0 bg-muted h-10 px-4 text-right align-middle text-xs font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap border-b border-border";
        const renderRawPagination = () => (
          <div className="flex items-center justify-between text-sm text-muted-foreground px-1">
            <span>{positions.length.toLocaleString()} rows{totalPages > 1 ? ` · page ${rawPage + 1} of ${totalPages}` : ''}</span>
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setRawPage(p => Math.max(0, p - 1))} disabled={rawPage === 0}>←</Button>
                <Button variant="outline" size="sm" onClick={() => setRawPage(p => Math.min(totalPages - 1, p + 1))} disabled={rawPage === totalPages - 1}>→</Button>
              </div>
            )}
          </div>
        );

        return (
          <div className="space-y-2">
            {renderRawPagination()}
            <div className="overflow-x-auto">
              <div className="overflow-y-auto max-h-[calc(100vh-150px)]">
                <table className="w-full caption-bottom text-sm">
                  <thead>
                    <tr>
                      <th className={thClass}>ID</th>
                      <th className={thClass}>{t('reports.time')}</th>
                      <th className={thClass}>Server Time</th>
                      <th className={thRClass}>Delay</th>
                      <th className={thClass}>Protocol</th>
                      <th className={thClass}>Valid</th>
                      <th className={thRClass}>{t('reports.latitude')}</th>
                      <th className={thRClass}>{t('reports.longitude')}</th>
                      <th className={thRClass}>Altitude</th>
                      <th className={thRClass}>{t('devices.speed')}</th>
                      <th className={thRClass}>Course</th>
                      <th className={thClass}>{t('devices.address')}</th>
                      {sortedAttrKeys.map(key => (
                        <th key={key} className={thRClass}>{key}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="[&_tr:last-child]:border-0">
                    {pagePositions.map((pos) => (
                      <tr key={pos.id} className="border-b transition-colors hover:bg-muted/50">
                        <td className="p-2 sm:p-4 align-middle text-muted-foreground font-mono text-xs">{pos.id}</td>
                        <td className="p-2 sm:p-4 align-middle whitespace-nowrap">{format(new Date(pos.fixTime), 'MMM d, HH:mm:ss', { locale: dateFnsLocale })}</td>
                        <td className="p-2 sm:p-4 align-middle whitespace-nowrap text-muted-foreground">{format(new Date(pos.serverTime), 'MMM d, HH:mm:ss', { locale: dateFnsLocale })}</td>
                        <td className="p-2 sm:p-4 align-middle text-right whitespace-nowrap">{(() => { const diff = Math.round((new Date(pos.serverTime).getTime() - new Date(pos.fixTime).getTime()) / 1000); return diff >= 60 ? `${Math.floor(diff / 60)}m ${diff % 60}s` : `${diff}s`; })()}</td>
                        <td className="p-2 sm:p-4 align-middle">{pos.protocol}</td>
                        <td className="p-2 sm:p-4 align-middle">{pos.valid ? 'Yes' : 'No'}</td>
                        <td className="p-2 sm:p-4 align-middle text-right">{pos.latitude.toFixed(6)}</td>
                        <td className="p-2 sm:p-4 align-middle text-right">{pos.longitude.toFixed(6)}</td>
                        <td className="p-2 sm:p-4 align-middle text-right">{pos.altitude.toFixed(1)}</td>
                        <td className="p-2 sm:p-4 align-middle text-right">{Math.round(pos.speed * 1.852)} km/h</td>
                        <td className="p-2 sm:p-4 align-middle text-right">{pos.course.toFixed(0)}°</td>
                        <td className="p-2 sm:p-4 align-middle max-w-[200px] truncate">{pos.address || ''}</td>
                        {sortedAttrKeys.map(key => (
                          <td key={key} className="p-2 sm:p-4 align-middle text-right whitespace-nowrap">
                            {pos.attributes?.[key] != null ? String(pos.attributes[key]) : ''}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {renderRawPagination()}
          </div>
        );
      }

      case 'temperature': {
        const rangeMin = parseRangeValue(tempRangeMin);
        const rangeMax = parseRangeValue(tempRangeMax);
        const hasRange = rangeMin != null || rangeMax != null;
        const positionsByDevice = new Map<number, Position[]>();
        for (const position of positions) {
          const devicePositions = positionsByDevice.get(position.deviceId) ?? [];
          devicePositions.push(position);
          positionsByDevice.set(position.deviceId, devicePositions);
        }

        return (
          <div className="space-y-10">
            {Array.from(positionsByDevice.entries()).map(([deviceId, devicePositions]) => {
              const summaries = summarizeTemperatures(devicePositions, rangeMin, rangeMax);
              const excursions = findExcursions(devicePositions, rangeMin, rangeMax);
              const chartData = thinForChart(
                [...devicePositions]
                  .sort((a, b) => new Date(a.fixTime).getTime() - new Date(b.fixTime).getTime())
                  .map(position => ({
                    time: new Date(position.fixTime).getTime(),
                    fixTime: position.fixTime,
                    ...getTemperatureReadings(position),
                  })),
                summaries.map(summary => summary.sensor),
                rangeMin,
                rangeMax,
              );

              return (
                <div key={deviceId} className="space-y-3">
                  <h3 className="font-medium px-1">{getDeviceName(deviceId)}</h3>
                  <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 px-1">
                    {summaries.map((summary, index) => (
                      <div key={summary.sensor} className="rounded-lg border p-3 space-y-1.5">
                        <div className="flex items-center gap-2 text-sm font-medium">
                          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }} />
                          {temperatureSensorLabel(t, summary.sensor)}
                        </div>
                        <div className="grid grid-cols-3 text-center">
                          {([
                            ['min', summary.min],
                            ['avg', summary.avg],
                            ['max', summary.max],
                          ] as const).map(([key, value]) => (
                            <div key={key}>
                              <div className="text-[11px] text-muted-foreground">{t(`reports.temperature.${key}`)}</div>
                              <div className={cn('font-semibold tabular-nums', isOutOfRange(value, rangeMin, rangeMax) && 'text-red-600 dark:text-red-400')}>
                                {value.toFixed(1)} °C
                              </div>
                            </div>
                          ))}
                        </div>
                        {hasRange && (
                          <div className={cn('text-xs', summary.outOfRange ? 'text-red-600 dark:text-red-400' : 'text-green-700 dark:text-green-400')}>
                            {summary.outOfRange
                              ? t('reports.temperature.outOfRange', { count: summary.outOfRange, duration: formatHours(summary.outOfRangeMs) })
                              : t('reports.temperature.allInRange')}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <ResponsiveContainer width="100%" height={340}>
                    <ComposedChart data={chartData} margin={{ top: 8, right: 24, left: 0, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="time"
                        type="number"
                        scale="time"
                        domain={['dataMin', 'dataMax']}
                        tickFormatter={(value) => format(new Date(value), 'MMM d, HH:mm', { locale: dateFnsLocale })}
                        tick={{ fontSize: 11 }}
                        minTickGap={48}
                      />
                      <YAxis domain={['auto', 'auto']} unit=" °C" tick={{ fontSize: 11 }} width={64} />
                      {hasRange && (
                        <ReferenceArea
                          y1={rangeMin ?? undefined}
                          y2={rangeMax ?? undefined}
                          fill="#16a34a"
                          fillOpacity={0.08}
                          stroke="#16a34a"
                          strokeOpacity={0.4}
                          strokeDasharray="4 4"
                          ifOverflow="extendDomain"
                        />
                      )}
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'hsl(var(--popover))',
                          color: 'hsl(var(--popover-foreground))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                        }}
                        labelStyle={{ color: 'hsl(var(--popover-foreground))' }}
                        labelFormatter={(value, payload) => {
                          const fixTime = payload[0]?.payload?.fixTime;
                          return format(new Date(fixTime ?? Number(value)), 'PPpp', { locale: dateFnsLocale });
                        }}
                        formatter={(value, name) => [`${Number(value).toFixed(1)} °C`, name]}
                      />
                      {summaries.length > 1 && <Legend />}
                      {summaries.map((summary, index) => (
                        <Line
                          key={summary.sensor}
                          type="monotone"
                          dataKey={summary.sensor}
                          name={temperatureSensorLabel(t, summary.sensor)}
                          stroke={CHART_COLORS[index % CHART_COLORS.length]}
                          strokeWidth={2}
                          connectNulls
                          isAnimationActive={false}
                          dot={(props: { cx?: number; cy?: number; value?: number; index?: number }) => {
                            const { cx, cy, value, index: pointIndex } = props;
                            const key = `${summary.sensor}-${pointIndex}`;
                            if (cx == null || cy == null || value == null || !isOutOfRange(value, rangeMin, rangeMax)) {
                              return <g key={key} />;
                            }
                            return <circle key={key} cx={cx} cy={cy} r={2.5} fill={rangeMax != null && value > rangeMax ? '#dc2626' : '#2563eb'} />;
                          }}
                        />
                      ))}
                    </ComposedChart>
                  </ResponsiveContainer>
                  {hasRange && (
                    <div className="space-y-2 px-1">
                      <h4 className="text-sm font-medium">
                        {t('reports.temperature.alerts')} ({excursions.length})
                      </h4>
                      {excursions.length === 0 ? (
                        <p className="text-sm text-muted-foreground">{t('reports.temperature.noAlerts')}</p>
                      ) : (
                        <div className="overflow-x-auto rounded-lg border">
                          <table className="w-full text-sm">
                            <thead className="bg-muted/50 text-muted-foreground">
                              <tr>
                                <th className="text-left font-medium px-3 py-2">{t('reports.temperature.alert')}</th>
                                {summaries.length > 1 && <th className="text-left font-medium px-3 py-2">{t('reports.temperature.sensorColumn')}</th>}
                                <th className="text-left font-medium px-3 py-2">{t('reports.temperature.start')}</th>
                                <th className="text-left font-medium px-3 py-2">{t('reports.temperature.end')}</th>
                                <th className="text-right font-medium px-3 py-2">{t('history.duration')}</th>
                                <th className="text-right font-medium px-3 py-2">{t('reports.temperature.peak')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {excursions.slice(-MAX_TEMPERATURE_ALERT_ROWS).reverse().map((excursion) => (
                                <tr key={`${excursion.sensor}-${excursion.start}`} className="border-t">
                                  <td className="px-3 py-2">
                                    <span className={cn(
                                      'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                                      excursion.direction === 'high'
                                        ? 'bg-red-500/15 text-red-700 dark:text-red-400'
                                        : 'bg-blue-500/15 text-blue-700 dark:text-blue-400',
                                    )}>
                                      {t(`reports.temperature.${excursion.direction === 'high' ? 'tooHigh' : 'tooLow'}`)}
                                    </span>
                                  </td>
                                  {summaries.length > 1 && <td className="px-3 py-2">{temperatureSensorLabel(t, excursion.sensor)}</td>}
                                  <td className="px-3 py-2 tabular-nums">{format(new Date(excursion.start), 'PP HH:mm', { locale: dateFnsLocale })}</td>
                                  <td className="px-3 py-2 tabular-nums">{format(new Date(excursion.end), 'PP HH:mm', { locale: dateFnsLocale })}</td>
                                  <td className="px-3 py-2 text-right tabular-nums">{excursion.durationMs ? formatHours(excursion.durationMs) : '< 1 min'}</td>
                                  <td className={cn('px-3 py-2 text-right font-semibold tabular-nums', excursion.direction === 'high' ? 'text-red-600 dark:text-red-400' : 'text-blue-600 dark:text-blue-400')}>
                                    {excursion.peak.toFixed(1)} °C
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {excursions.length > MAX_TEMPERATURE_ALERT_ROWS && (
                            <p className="text-xs text-muted-foreground px-3 py-2 border-t">
                              {t('reports.temperature.moreAlerts', { count: excursions.length - MAX_TEMPERATURE_ALERT_ROWS })}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      }

      case 'battery-voltage': {
        const positionsByDevice = new Map<number, Position[]>();
        for (const position of positions) {
          const devicePositions = positionsByDevice.get(position.deviceId) ?? [];
          devicePositions.push(position);
          positionsByDevice.set(position.deviceId, devicePositions);
        }

        return (
          <div className="space-y-8">
            {Array.from(positionsByDevice.entries()).map(([deviceId, devicePositions]) => {
              const chartData = [...devicePositions]
                .sort((a, b) => new Date(a.fixTime).getTime() - new Date(b.fixTime).getTime())
                .map(position => ({
                  time: new Date(position.fixTime).getTime(),
                  fixTime: position.fixTime,
                  voltage: getVehicleBatteryVoltage(position),
                }));

              return (
                <div key={deviceId} className="space-y-2">
                  <h3 className="font-medium px-1">{getDeviceName(deviceId)}</h3>
                  <ResponsiveContainer width="100%" height={340}>
                    <ComposedChart data={chartData} margin={{ top: 8, right: 24, left: 0, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="time"
                        type="number"
                        scale="time"
                        domain={['dataMin', 'dataMax']}
                        tickFormatter={(value) => format(new Date(value), 'MMM d, HH:mm', { locale: dateFnsLocale })}
                        tick={{ fontSize: 11 }}
                        minTickGap={48}
                      />
                      <YAxis
                        dataKey="voltage"
                        domain={['auto', 'auto']}
                        unit=" V"
                        tick={{ fontSize: 11 }}
                        width={64}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'hsl(var(--popover))',
                          color: 'hsl(var(--popover-foreground))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                        }}
                        labelStyle={{ color: 'hsl(var(--popover-foreground))' }}
                        itemStyle={{ color: 'hsl(var(--popover-foreground))' }}
                        labelFormatter={(value, payload) => {
                          const fixTime = payload[0]?.payload?.fixTime;
                          return format(new Date(fixTime ?? Number(value)), 'PPpp', { locale: dateFnsLocale });
                        }}
                        formatter={(value) => [`${Number(value).toFixed(2)}`, t('reports.batteryVoltage')]}
                      />
                      <Line
                        type="monotone"
                        dataKey="voltage"
                        name={t('reports.batteryVoltage')}
                        stroke={CHART_COLORS[0]}
                        strokeWidth={2}
                        dot={false}
                        connectNulls={false}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              );
            })}
          </div>
        );
      }

      case 'activity':
        return (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground px-1">{t('reports.charts.activityNote')}</p>
            <ResponsiveContainer width="100%" height={360}>
              <ComposedChart data={activityChartData} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="dist" orientation="left" tick={{ fontSize: 11 }} unit=" km" />
                <YAxis yAxisId="trips" orientation="right" allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(val, name) => name === t('history.distance') ? `${val} km` : val} />
                <Legend />
                <Bar yAxisId="dist" dataKey="distance" name={t('history.distance')} fill={CHART_COLORS[0]} radius={[3,3,0,0]} />
                <Line yAxisId="trips" type="monotone" dataKey="trips" name={t('reports.tripCount')} stroke={CHART_COLORS[1]} strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        );

      case 'events-chart': {
        const topTypes = eventsChartData.slice(0, 12);
        return (
          <div className="space-y-2">
            <ResponsiveContainer width="100%" height={Math.max(260, topTypes.length * 36)}>
              <BarChart data={topTypes} layout="vertical" margin={{ top: 0, right: 24, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis type="category" dataKey="type"
                  tickFormatter={(v) => {
                    const key = `notifications.events.${v}`;
                    const tr = t(key, { defaultValue: v });
                    return tr.length > 22 ? tr.slice(0, 21) + '…' : tr;
                  }}
                  width={150} tick={{ fontSize: 11 }} />
                <Tooltip
                  formatter={(val) => [val, t('reports.charts.count')]}
                  labelFormatter={(v) => t(`notifications.events.${v}`, { defaultValue: v })}
                />
                <Bar dataKey="count" name={t('reports.charts.count')} radius={[0,3,3,0]}>
                  {topTypes.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        );
      }

      case 'driver-chart':
        return (
          <div className="space-y-6">
            <div>
              <p className="text-xs text-muted-foreground mb-2 px-1">{t('history.distance')} (km)</p>
              <ResponsiveContainer width="100%" height={Math.max(260, driverChartData.length * 36)}>
                <BarChart data={driverChartData} layout="vertical" margin={{ top: 0, right: 24, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} unit=" km" />
                  <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(val) => [`${val} km`, t('history.distance')]} labelFormatter={(_, p) => p[0]?.payload?.fullName ?? ''} />
                  <Bar dataKey="distance" name={t('history.distance')} radius={[0,3,3,0]}>
                    {driverChartData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-2 px-1">{t('reports.tripCount')}</p>
              <ResponsiveContainer width="100%" height={Math.max(260, driverChartData.length * 36)}>
                <BarChart data={driverChartData} layout="vertical" margin={{ top: 0, right: 24, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(val) => [val, t('reports.tripCount')]} labelFormatter={(_, p) => p[0]?.payload?.fullName ?? ''} />
                  <Bar dataKey="trips" name={t('reports.tripCount')} fill={CHART_COLORS[2]} radius={[0,3,3,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        );
    }
  };

  return (
    <DashboardLayout headerContent={headerContent}>
      <div className="p-6">
        {/* Print-only title with report info */}
        <div className="print-title hidden mb-4">
          <div className="print-title-header">
            <img src={brandLogo || '/favicon.svg'} alt={brandTitle} className="print-logo" />
            <div>
              <h1 className="text-xl font-bold">{t('reports.title')} - {t(getReportTypeLabelKey(reportType))}</h1>
              {selectedDeviceId !== 'all' && (
                <p className="text-sm text-muted-foreground">{devices.find(d => d.id.toString() === selectedDeviceId)?.name}</p>
              )}
              {dateRange?.from && dateRange?.to && (
                <p className="text-sm text-muted-foreground">
                  {format(dateRange.from, 'MMM d, yyyy', { locale: dateFnsLocale })} — {format(dateRange.to, 'MMM d, yyyy', { locale: dateFnsLocale })}
                </p>
              )}
            </div>
          </div>
        </div>
        {isGenerating ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          renderTable()
        )}
      </div>
    </DashboardLayout>
  );
};

export default Reports;
